import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import Logo from "../assets/layerbound-white.png";
import awsConfig from "../aws-config";
import { resolveAuthToken } from "../utils/authToken";
import {
  buildLocalServiceQuoteBlueprint,
  buildTrackedQuoteLinks,
  isLocalServiceQuoteFlow,
} from "../utils/localServiceQuote";
import "./SystemGeneratorPage.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";

const API_URL = awsConfig.api_url_get_menu;
const BUSINESS_API_URL = awsConfig.api_url_create_business;
const PRESIGN_API = awsConfig.api_url_presign_logo;
const MAX_ASSET_SIZE = 200 * 1024; // 200 KB
const MAX_IMAGE_COUNT = 3;
const DEFAULT_RECRUITMENT_VALID_DAYS = 7;

const toIsoDateString = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const formatValidUntilLabel = (isoDate) => {
  if (!isoDate) return "";
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(parsed);
};

const defaultValidUntilIso = () => {
  const date = new Date();
  date.setDate(date.getDate() + DEFAULT_RECRUITMENT_VALID_DAYS);
  return toIsoDateString(date);
};

function formatSuggestionKey(key) {
  const map = {
    primary: "Hero message",
    headline: "Subheadline",
    time_of_day: "Time of day",
    day_of_week: "Day of week",
    seasonal: "Seasonal",
    special_occasion: "Occasion",
    cuisine: "Category",
  };
  return map[key] || key.replace(/_/g, " ");
}

function BlueprintPreview({
  blueprint,
  setBlueprint,
  onResetRecruitmentQuestions,
  canResetRecruitmentQuestions,
  minimalRecruitment = false,
  intakeMode = false,
}) {
  const [showDetails, setShowDetails] = React.useState(minimalRecruitment);
  const [isEditingItems, setIsEditingItems] = React.useState(false);
  const [qualError, setQualError] = React.useState("");
  const [dragState, setDragState] = React.useState(null);
  const qualificationIndices = React.useMemo(() => {
    if (!blueprint?.components) return [];
    return blueprint.components
      .map((component, idx) => ((component?.type || "").toLowerCase() === "qualification_section" ? idx : null))
      .filter((idx) => idx != null);
  }, [blueprint?.components]);
  const qualificationSections = qualificationIndices.map((idx) => ({
    index: idx,
    section: blueprint.components[idx],
  }));
  const normalizeCategory = React.useCallback(
    (value) => String(value || "").trim().toLowerCase(),
    []
  );
  const toTitleCase = (value) =>
    String(value || "")
      .replace(/_/g, " ")
      .split(" ")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  const categoryOptions = React.useMemo(() => {
    if (intakeMode) {
      const options = [];
      const seen = new Set();
      const addOption = (value) => {
        const normalized = normalizeCategory(value);
        if (!normalized || seen.has(normalized)) return;
        seen.add(normalized);
        options.push({ value: normalized, label: toTitleCase(normalized) });
      };
      const required = Array.isArray(blueprint?.flowSpec?.required_categories)
        ? blueprint.flowSpec.required_categories
        : [];
      required.forEach(addOption);
      (blueprint?.components || []).forEach((component) => {
        if ((component?.type || "").toLowerCase() !== "qualification_section") return;
        (component?.questions || []).forEach((question) => addOption(question?.category));
      });
      return options.length
        ? options
        : [{ value: "other", label: "Other" }];
    }
    return [
      { value: "constraints", label: "Constraints" },
      { value: "availability", label: "Availability" },
      { value: "experience", label: "Experience" },
      { value: "other", label: "Other" },
      { value: "role_title", label: "Role title" },
      { value: "contact", label: "Contact" },
    ];
  }, [intakeMode, blueprint?.flowSpec?.required_categories, blueprint?.components, normalizeCategory]);
  const countQualQuestions = React.useCallback(
    (components) => {
      if (!Array.isArray(components)) return 0;
      return components.reduce((total, component) => {
        if ((component?.type || "").toLowerCase() !== "qualification_section") return total;
        const questions = Array.isArray(component.questions) ? component.questions : [];
        const count = questions.filter((q) => (q?.category || "") !== "contact").length;
        return total + count;
      }, 0);
    },
    []
  );
  const countQualCategoryCoverage = React.useCallback(
    (components, requiredSet) => {
      if (!Array.isArray(components) || !requiredSet || requiredSet.size === 0) return 0;
      const covered = new Set();
      components.forEach((component) => {
        if ((component?.type || "").toLowerCase() !== "qualification_section") return;
        const questions = Array.isArray(component.questions) ? component.questions : [];
        questions.forEach((question) => {
          const category = normalizeCategory(question?.category);
          if (!category || category === "contact") return;
          if (requiredSet.has(category)) covered.add(category);
        });
      });
      return covered.size;
    },
    [normalizeCategory]
  );
  if (!blueprint) return null;
  const templateLabel = blueprint.templateMeta?.label || blueprint.template;
  const supportedPayments = blueprint.templateMeta?.supportedPayments || [];
  const requiredCategories = Array.isArray(blueprint.flowSpec?.required_categories)
    ? blueprint.flowSpec.required_categories
    : [];
  const requiredCategorySet = new Set(
    requiredCategories
      .map((category) => normalizeCategory(category))
      .filter((category) => category && !category.includes("contact"))
  );
  const requiredCategoryCount = requiredCategorySet.size;
  const minRequiredQuestions = Number(blueprint.templateMeta?.minRequiredQuestions || 2) || 2;
  const qualQuestionCount = countQualQuestions(blueprint.components);
  const readinessCount =
    requiredCategoryCount > 0
      ? countQualCategoryCoverage(blueprint.components, requiredCategorySet)
      : qualQuestionCount;
  const readinessTarget = requiredCategoryCount > 0 ? requiredCategoryCount : minRequiredQuestions;
  const componentList = (blueprint.components || []).filter(
    (component) => component && component.type !== "booking_settings"
  );
  const isRecruitmentTemplate = (blueprint.template || "").toLowerCase() === "recruitment";
  const showQuestionEditor = minimalRecruitment || showDetails;
  const suggestions = Object.entries(blueprint.contextSuggestions || {}).filter(([key, value]) => {
    if (!value) return false;
    // Drop bland defaults that look mismatched in the UI (e.g. "American")
    if (key === "cuisine" && value.toLowerCase() === "american") return false;
    return true;
  });

  const updateItem = (index, updates) => {
    if (!setBlueprint) return;
    setBlueprint((prev) => {
      if (!prev) return prev;
      const items = Array.isArray(prev.items) ? [...prev.items] : [];
      if (!items[index]) return prev;
      items[index] = { ...items[index], ...updates };
      return { ...prev, items };
    });
  };

  const removeItem = (index) => {
    if (!setBlueprint) return;
    setBlueprint((prev) => {
      if (!prev) return prev;
      const items = Array.isArray(prev.items) ? [...prev.items] : [];
      if (!items[index]) return prev;
      items.splice(index, 1);
      return { ...prev, items };
    });
  };

  const updateQualificationSection = (sectionIndex, updates) => {
    if (!setBlueprint) return;
    setBlueprint((prev) => {
      if (!prev) return prev;
      const components = Array.isArray(prev.components) ? [...prev.components] : [];
      const targetIndex = qualificationIndices[sectionIndex];
      if (targetIndex == null || !components[targetIndex]) return prev;
      components[targetIndex] = { ...components[targetIndex], ...updates };
      return { ...prev, components };
    });
  };

  const updateQualificationQuestion = (sectionIndex, questionIndex, updates) => {
    if (!setBlueprint) return;
    setBlueprint((prev) => {
      if (!prev) return prev;
      const components = Array.isArray(prev.components) ? [...prev.components] : [];
      const targetIndex = qualificationIndices[sectionIndex];
      if (targetIndex == null || !components[targetIndex]) return prev;
      const section = { ...components[targetIndex] };
      const questions = Array.isArray(section.questions) ? [...section.questions] : [];
      if (!questions[questionIndex]) return prev;
      questions[questionIndex] = { ...questions[questionIndex], ...updates };
      section.questions = questions;
      components[targetIndex] = section;
      return { ...prev, components };
    });
  };

  const addQualificationQuestion = (sectionIndex) => {
    if (!setBlueprint) return;
    setBlueprint((prev) => {
      if (!prev) return prev;
      const components = Array.isArray(prev.components) ? [...prev.components] : [];
      const currentCount = countQualQuestions(components);
      if (currentCount >= 10) {
        setQualError("Maximum 10 questions allowed.");
        return prev;
      }
      const targetIndex = qualificationIndices[sectionIndex];
      if (targetIndex == null || !components[targetIndex]) return prev;
      const section = { ...components[targetIndex] };
      const questions = Array.isArray(section.questions) ? [...section.questions] : [];
      questions.push({
        id: `question_${questions.length + 1}`,
        label: "New question",
        input: "text",
        category: "other",
      });
      section.questions = questions;
      components[targetIndex] = section;
      setQualError("");
      return { ...prev, components };
    });
  };

  const duplicateQualificationQuestion = (sectionIndex, questionIndex) => {
    if (!setBlueprint) return;
    setBlueprint((prev) => {
      if (!prev) return prev;
      const components = Array.isArray(prev.components) ? [...prev.components] : [];
      const currentCount = countQualQuestions(components);
      if (currentCount >= 10) {
        setQualError("Maximum 10 questions allowed.");
        return prev;
      }
      const targetIndex = qualificationIndices[sectionIndex];
      if (targetIndex == null || !components[targetIndex]) return prev;
      const section = { ...components[targetIndex] };
      const questions = Array.isArray(section.questions) ? [...section.questions] : [];
      if (!questions[questionIndex]) return prev;
      const duplicate = { ...questions[questionIndex], id: `question_${questions.length + 1}` };
      questions.splice(questionIndex + 1, 0, duplicate);
      section.questions = questions;
      components[targetIndex] = section;
      setQualError("");
      return { ...prev, components };
    });
  };

  const removeQualificationQuestion = (sectionIndex, questionIndex) => {
    if (!setBlueprint) return;
    setBlueprint((prev) => {
      if (!prev) return prev;
      const components = Array.isArray(prev.components) ? [...prev.components] : [];
      const currentCount = countQualQuestions(components);
      if (currentCount <= 2) {
        setQualError("Minimum 2 questions required.");
        return prev;
      }
      const targetIndex = qualificationIndices[sectionIndex];
      if (targetIndex == null || !components[targetIndex]) return prev;
      const section = { ...components[targetIndex] };
      const questions = Array.isArray(section.questions) ? [...section.questions] : [];
      if (!questions[questionIndex]) return prev;
      questions.splice(questionIndex, 1);
      section.questions = questions;
      components[targetIndex] = section;
      setQualError("");
      return { ...prev, components };
    });
  };

  const moveQualificationQuestion = (sectionIndex, fromIndex, toIndex) => {
    if (!setBlueprint) return;
    if (fromIndex === toIndex) return;
    setBlueprint((prev) => {
      if (!prev) return prev;
      const components = Array.isArray(prev.components) ? [...prev.components] : [];
      const targetIndex = qualificationIndices[sectionIndex];
      if (targetIndex == null || !components[targetIndex]) return prev;
      const section = { ...components[targetIndex] };
      const questions = Array.isArray(section.questions) ? [...section.questions] : [];
      if (!questions[fromIndex] || !questions[toIndex]) return prev;
      const [moved] = questions.splice(fromIndex, 1);
      questions.splice(toIndex, 0, moved);
      section.questions = questions;
      components[targetIndex] = section;
      return { ...prev, components };
    });
  };

  const handleDragStart = (sectionIndex, questionIndex) => {
    setDragState({ sectionIndex, questionIndex });
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (sectionIndex, questionIndex) => {
    if (!dragState) return;
    if (dragState.sectionIndex !== sectionIndex) {
      setDragState(null);
      return;
    }
    moveQualificationQuestion(sectionIndex, dragState.questionIndex, questionIndex);
    setDragState(null);
  };

  const addQualificationSection = () => {
    if (!setBlueprint) return;
    setBlueprint((prev) => {
      if (!prev) return prev;
      const components = Array.isArray(prev.components) ? [...prev.components] : [];
      const currentCount = countQualQuestions(components);
      if (currentCount >= 10) {
        setQualError("Maximum 10 questions allowed.");
        return prev;
      }
      components.push({
        type: "qualification_section",
        title: "New section",
        description: "",
        questions: [
          {
            id: "question_1",
            label: "New question",
            input: "text",
            category: "other",
          },
        ],
      });
      setQualError("");
      return { ...prev, components };
    });
  };

  const removeQualificationSection = (sectionIndex) => {
    if (!setBlueprint) return;
    setBlueprint((prev) => {
      if (!prev) return prev;
      const components = Array.isArray(prev.components) ? [...prev.components] : [];
      const targetIndex = qualificationIndices[sectionIndex];
      if (targetIndex == null || !components[targetIndex]) return prev;
      const currentCount = countQualQuestions(components);
      const section = components[targetIndex];
      const sectionCount = Array.isArray(section?.questions)
        ? section.questions.filter((q) => (q?.category || "") !== "contact").length
        : 0;
      if (currentCount - sectionCount < 2) {
        setQualError("Minimum 2 questions required.");
        return prev;
      }
      components.splice(targetIndex, 1);
      setQualError("");
      return { ...prev, components };
    });
  };

  const formatOptions = (options) => (Array.isArray(options) ? options.join(", ") : "");
  const parseOptions = (value) =>
    value
      .split(/,|\n/)
      .map((item) => item.trim())
      .filter(Boolean);
  const formatBlockerAnswers = (answers) =>
    Array.isArray(answers) ? answers.join(", ") : "";
  const parseBlockerAnswers = (value) =>
    value
      .split(/,|\n/)
      .map((item) => item.trim())
      .filter(Boolean);
  const getOptionsInputValue = (question) =>
    typeof question?.optionsText === "string"
      ? question.optionsText
      : formatOptions(question?.options || []);
  const getBlockersInputValue = (question) =>
    typeof question?.blockerAnswersText === "string"
      ? question.blockerAnswersText
      : formatBlockerAnswers(question?.blockerAnswers || []);

  const clampText = (value, limit) => (value || "").slice(0, limit);

  const sanitizePrice = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return 0.01;
    const safe = Math.max(0.01, num);
    return parseFloat(safe.toFixed(2));
  };
  return (
    <section className="system-gen__result">
      {!minimalRecruitment && (
        <>
          <header className="system-gen__result-header">
            <div>
              <p className="system-gen__eyebrow">
                {isRecruitmentTemplate ? "Generated intake flow" : "Generated storefront"}
              </p>
              <h2>{templateLabel}</h2>
            </div>
            <div className="system-gen__result-meta">
              <span>Kind · {blueprint.blueprintKind || "menu"}</span>
              <span>Template · {blueprint.template}</span>
              <span>Payment · {blueprint.paymentMethod}</span>
            </div>
            <button
              type="button"
              className="btn btn-secondary btn-secondary--ghost"
              onClick={() => setShowDetails((prev) => !prev)}
            >
              {showDetails ? "Hide details" : "Show details"}
            </button>
          </header>
          {supportedPayments.length > 0 && (
            <div className="system-gen__pill-row">
              {supportedPayments.map((method) => (
                <span className="system-gen__pill" key={method}>
                  Supports {method}
                </span>
              ))}
            </div>
          )}
          <div className="system-gen__grid">
            {showDetails && blueprint.bookingSettings && (
              <div className="system-gen__panel">
                <h3>{isRecruitmentTemplate ? "Intake settings" : "Booking settings"}</h3>
                <ul>
                  <li>
                    <strong>Interview</strong>
                    <span>{blueprint.bookingSettings?.resourceLabel || "Slot"}</span>
                  </li>
                  <li>
                    <strong>Duration</strong>
                    <span>{blueprint.bookingSettings?.slotDurationMinutes || 60} mins</span>
                  </li>
                  <li>
                    <strong>Capacity</strong>
                    <span>{blueprint.bookingSettings?.maxSimultaneousBookings || 10} slots</span>
                  </li>
                  {blueprint.bookingSettings?.notes && (
                    <li>
                      <strong>Notes</strong>
                      <span>{blueprint.bookingSettings?.notes}</span>
                    </li>
                  )}
                </ul>
              </div>
            )}
            <div className="system-gen__panel">
              <div className="system-gen__panel-header">
                <h3>Catalog preview</h3>
                <button
                  type="button"
                  className="btn btn-secondary btn-secondary--ghost"
                  onClick={() => setIsEditingItems((prev) => !prev)}
                >
                  {isEditingItems ? "Done" : "Edit"}
                </button>
              </div>
              <ul className="system-gen__items">
                {(blueprint.items || []).slice(0, showDetails ? 6 : 3).map((item, idx) => (
                  <li key={item.id || idx}>
                    {isEditingItems ? (
                      <div className="system-gen__item-edit">
                        <div className="system-gen__item-edit-header">
                          <span className="system-gen__pill">Item {idx + 1}</span>
                          <button
                            type="button"
                            className="btn btn-secondary btn-secondary--ghost system-gen__item-remove"
                            onClick={() => removeItem(idx)}
                          >
                            Remove
                          </button>
                        </div>
                        <input
                          className="system-gen__item-input"
                          type="text"
                          maxLength={80}
                          value={item.name || ""}
                          onChange={(e) => updateItem(idx, { name: clampText(e.target.value, 80) })}
                          placeholder="Item name"
                        />
                        <textarea
                          className="system-gen__item-input"
                          rows={3}
                          maxLength={240}
                          value={item.description || ""}
                          onChange={(e) =>
                            updateItem(idx, { description: clampText(e.target.value, 240) })
                          }
                          placeholder="Short description"
                        />
                        <div className="system-gen__item-price-edit">
                          <span>Price</span>
                          <input
                            className="system-gen__item-input"
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={item.price ?? ""}
                            onChange={(e) => updateItem(idx, { price: sanitizePrice(e.target.value) })}
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div>
                          <p className="system-gen__item-name">{item.name}</p>
                          {item.description && (
                            <p className="system-gen__item-desc">{item.description}</p>
                          )}
                        </div>
                        <span className="system-gen__price">
                          {blueprint.currency?.symbol || "$"}
                          {Number(item.price || 0).toFixed(2)}
                        </span>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {showDetails && componentList.length > 0 && (
            <div className="system-gen__panel">
              <h3>Suggested components</h3>
              <ul className="system-gen__component-list">
                {componentList.map((component, index) => (
                  <li key={`${component.type}-${index}`}>
                    <div className="system-gen__component-type">{component.type}</div>
                    {component.title && <p className="system-gen__component-title">{component.title}</p>}
                    {Array.isArray(component.items) && component.items.length > 0 && (
                      <ul className="system-gen__component-items">
                        {component.items.slice(0, 3).map((entry, idx) => (
                          <li key={idx} className="system-gen__component-entry">
                            <strong>{entry.label}</strong>
                            {entry.description && (
                              <p className="system-gen__component-desc">{entry.description}</p>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      {showQuestionEditor && isRecruitmentTemplate && (
        <>
          <div className="system-gen__step">
            <span className="system-gen__step-label">02</span>
            <span className="system-gen__step-title">Review questions</span>
          </div>
          <div className="system-gen__panel">
            <div className="system-gen__panel-header">
              <div className="system-gen__panel-title">
                <h3>Qualification questions</h3>
                <span
                  className={`system-gen__badge ${
                    readinessCount >= readinessTarget ? "system-gen__badge--ready" : ""
                  }`}
                >
                  Call readiness: {readinessCount}/{readinessTarget}
                </span>
              </div>
              <div className="system-gen__panel-actions">
                {canResetRecruitmentQuestions && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-secondary--ghost"
                    onClick={onResetRecruitmentQuestions}
                  >
                    Reset to defaults
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-secondary btn-secondary--ghost"
                  onClick={addQualificationSection}
                >
                  Add section
                </button>
              </div>
            </div>
          <p className="system-gen__subtitle">
            Shape the questions your client actually cares about. Drag to reorder. 2–10 questions
            recommended. Contact questions don’t count.
          </p>
          {qualificationSections.length === 0 ? (
            <p className="system-gen__subtitle">No qualification sections yet.</p>
          ) : (
            <div className="system-gen__qual-list">
              {qualError && <p className="system-gen__error">{qualError}</p>}
              {qualificationSections.map(({ index, section }, sectionIndex) => (
                <div className="system-gen__qual-section" key={`${index}-${section?.title || sectionIndex}`}>
                  <div className="system-gen__qual-section-header">
                    <div className="system-gen__qual-section-actions">
                      <button
                        type="button"
                        className="btn btn-secondary btn-secondary--ghost"
                        onClick={() =>
                          updateQualificationSection(sectionIndex, { isCollapsed: !section?.isCollapsed })
                        }
                      >
                        {section?.isCollapsed ? "Expand" : "Collapse"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-secondary--ghost"
                        onClick={() => removeQualificationSection(sectionIndex)}
                      >
                        Remove section
                      </button>
                    </div>
                    <label className="system-gen__field system-gen__field--wide">
                      <span>Section title</span>
                      <input
                        type="text"
                        value={section?.title || ""}
                        onChange={(e) => updateQualificationSection(sectionIndex, { title: e.target.value })}
                        placeholder="e.g. Eligibility, Availability, Role fit"
                      />
                    </label>
                    <label className="system-gen__field system-gen__field--wide">
                      <span>Section description (optional)</span>
                      <input
                        type="text"
                        value={section?.description || ""}
                        onChange={(e) => updateQualificationSection(sectionIndex, { description: e.target.value })}
                        placeholder={
                          intakeMode
                            ? "Short context to guide respondents"
                            : "Short context to guide candidates"
                        }
                      />
                    </label>
                  </div>
                  {!section?.isCollapsed && (
                  <div className="system-gen__qual-questions">
                    {(section?.questions || []).map((question, questionIndex) => (
                      <div
                        className={`system-gen__qual-question${dragState?.sectionIndex === sectionIndex && dragState?.questionIndex === questionIndex ? " is-dragging" : ""}`}
                        key={`${question?.id || questionIndex}`}
                        draggable
                        onDragStart={() => handleDragStart(sectionIndex, questionIndex)}
                        onDragOver={handleDragOver}
                        onDrop={() => handleDrop(sectionIndex, questionIndex)}
                      >
                        <div className="system-gen__qual-drag">
                          <span className="system-gen__drag-handle" aria-hidden="true">⋮⋮</span>
                          <span className="system-gen__qual-index">Q{questionIndex + 1}</span>
                        </div>
                        <label className="system-gen__field system-gen__field--wide">
                          <span>Question</span>
                          <input
                            type="text"
                            value={question?.label || ""}
                            onChange={(e) =>
                              updateQualificationQuestion(sectionIndex, questionIndex, { label: e.target.value })
                            }
                            placeholder="Type the exact question you want to ask"
                          />
                        </label>
                        {question?.input !== "select" && (
                          <label className="system-gen__field system-gen__field--wide">
                            <span>Placeholder (optional)</span>
                            <input
                              type="text"
                              value={question?.placeholder || ""}
                              onChange={(e) =>
                                updateQualificationQuestion(sectionIndex, questionIndex, {
                                  placeholder: e.target.value,
                                })
                              }
                              placeholder={
                                intakeMode
                                  ? "Shown inside the field before the user types"
                                  : "Shown inside the field before the candidate types"
                              }
                            />
                          </label>
                        )}
                        <div className="system-gen__qual-row">
                          <label className="system-gen__field">
                            <span>Answer type</span>
                            <select
                              value={question?.input || "text"}
                              onChange={(e) =>
                                updateQualificationQuestion(sectionIndex, questionIndex, { input: e.target.value })
                              }
                            >
                              <option value="text">Text</option>
                              <option value="select">Select</option>
                              <option value="textarea">Textarea</option>
                            </select>
                          </label>
                          <label className="system-gen__field">
                            <span>Category</span>
                            <select
                              value={question?.category || "other"}
                              onChange={(e) =>
                                updateQualificationQuestion(sectionIndex, questionIndex, { category: e.target.value })
                              }
                            >
                              {categoryOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="system-gen__field system-gen__field--inline">
                            <span>Optional</span>
                            <input
                              type="checkbox"
                              checked={Boolean(question?.optional)}
                              onChange={(e) =>
                                updateQualificationQuestion(sectionIndex, questionIndex, { optional: e.target.checked })
                              }
                            />
                          </label>
                          <button
                            type="button"
                            className="btn btn-secondary btn-secondary--ghost"
                            onClick={() => removeQualificationQuestion(sectionIndex, questionIndex)}
                          >
                            Remove
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-secondary--ghost"
                            onClick={() => duplicateQualificationQuestion(sectionIndex, questionIndex)}
                          >
                            Duplicate
                          </button>
                        </div>
                        {question?.input === "select" && (
                          <label className="system-gen__field system-gen__field--wide">
                            <span>Options</span>
                            <input
                              type="text"
                              value={getOptionsInputValue(question)}
                              onChange={(e) =>
                                updateQualificationQuestion(sectionIndex, questionIndex, {
                                  optionsText: e.target.value,
                                })
                              }
                              onBlur={(e) =>
                                updateQualificationQuestion(sectionIndex, questionIndex, {
                                  options: parseOptions(e.target.value),
                                })
                              }
                              placeholder="Comma or new line separated e.g. Yes, No, Maybe"
                            />
                          </label>
                        )}
                        <label className="system-gen__field system-gen__field--wide">
                          <span className="system-gen__help-label">
                            Blocker answers (optional)
                            <span
                              className="system-gen__help-icon"
                              title={
                                intakeMode
                                  ? "If a respondent selects any of these answers, they will be flagged as Likely Mismatch (red) in the queue."
                                  : "If a candidate selects any of these answers, they will be flagged as Likely Mismatch (red) in the screening queue."
                              }
                              aria-label="Blocker answers help"
                            >
                              i
                            </span>
                          </span>
                          <input
                            type="text"
                            value={getBlockersInputValue(question)}
                            onChange={(e) =>
                              updateQualificationQuestion(sectionIndex, questionIndex, {
                                blockerAnswersText: e.target.value,
                              })
                            }
                            onBlur={(e) =>
                              updateQualificationQuestion(sectionIndex, questionIndex, {
                                blockerAnswers: parseBlockerAnswers(e.target.value),
                              })
                            }
                            placeholder="Comma or new line separated e.g. No, Require sponsorship"
                          />
                        </label>
                      </div>
                    ))}
                  </div>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary btn-secondary--ghost"
                    onClick={() => addQualificationQuestion(sectionIndex)}
                  >
                    Add question
                  </button>
                </div>
              ))}
            </div>
          )}
          </div>
        </>
      )}
      {!minimalRecruitment && showDetails && suggestions.length > 0 && (
        <div className="system-gen__panel">
          <h3>Context suggestions</h3>
          <dl className="system-gen__suggestions">
            {suggestions.map(([key, value]) => (
              <div key={key}>
                <dt>{formatSuggestionKey(key)}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </section>
  );
}

function ContextSuggestionsEditor({ blueprint, setBlueprint }) {
  if (!blueprint || !setBlueprint) return null;
  const suggestions = blueprint.contextSuggestions || {};
  const fields = [
    {
      key: "primary",
      label: "Hero/context line",
      placeholder: "Servicing at Canary Wharf, E14 and nearby",
      maxLength: 140,
    },
    {
      key: "headline",
      label: "Secondary line",
      placeholder: "Book fittings, repairs, or coaching in minutes",
      maxLength: 140,
    },
    { key: "special_occasion", label: "Occasion", placeholder: "Great for private tastings" },
    { key: "time_of_day", label: "Time of day", placeholder: "Perfect for weekday lunches" },
    { key: "day_of_week", label: "Day of week", placeholder: "Taco Tuesday treat" },
    { key: "seasonal", label: "Seasonal", placeholder: "Autumn comfort specials" },
    { key: "cuisine", label: "Cuisine / style", placeholder: "Mediterranean fusion" },
  ];

  const updateSuggestion = (key, value) => {
    setBlueprint((prev) => {
      if (!prev) return prev;
      const next = { ...(prev.contextSuggestions || {}) };
      next[key] = value;
      return { ...prev, contextSuggestions: next };
    });
  };

  return (
    <div className="system-gen__panel">
      <div className="system-gen__panel-header">
        <div>
          <p className="system-gen__eyebrow">Context</p>
          <h3>Headline and servicing copy</h3>
        </div>
        <p className="system-gen__subtitle">
          Edit the AI messaging that powers the context bar and hero (e.g. servicing at your area,
          seasonal hooks, cuisine).
        </p>
      </div>
      <div className="system-gen__grid system-gen__grid--three">
        {fields.map((field) => (
          <label className="system-gen__field" key={field.key}>
            <span>{field.label}</span>
            <input
              type="text"
              maxLength={field.maxLength || 120}
              value={suggestions[field.key] || ""}
              placeholder={field.placeholder}
              onChange={(e) => updateSuggestion(field.key, e.target.value)}
            />
          </label>
        ))}
      </div>
    </div>
  );
}

function TemplateMetaEditor({ blueprint, setBlueprint, business, rec2RecMode = false, intakeMode = false }) {
  if (!blueprint || !setBlueprint) return null;
  const meta = blueprint.templateMeta || {};
  const templateName = (blueprint.template || "").toLowerCase();
  const isRecruitment = templateName === "recruitment";
  const isRec2Rec = isRecruitment && rec2RecMode;
  const contactRequired = Boolean(meta.contactRequired);
  const nameRequired = Boolean(meta.nameRequired);
  const minRequiredQuestions = Number(meta.minRequiredQuestions || 2) || 2;

  const updateMeta = (updates) => {
    setBlueprint((prev) => {
      if (!prev) return prev;
      return { ...prev, templateMeta: { ...(prev.templateMeta || {}), ...updates } };
    });
  };

  const handleQuickAnswersChange = (value) => {
    const lines = value
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .slice(0, 6);
    updateMeta({ quickAnswers: lines });
  };

  const quickAnswersText = (meta.quickAnswers || []).join("\n");

  return (
    <div className="system-gen__panel">
      <div className="system-gen__panel-header">
        <div>
          <p className="system-gen__eyebrow">Page copy</p>
          <h3>Hero, CTAs, and section headers</h3>
        </div>
        <p className="system-gen__subtitle">
          Edit the visible text before publishing. Leave blank to keep the AI defaults.
        </p>
      </div>
      <div className="system-gen__grid system-gen__grid--three">
        {isRec2Rec ? (
          <>
            <label className="system-gen__field">
              <span>Recruiter name (optional)</span>
              <input
                type="text"
                maxLength={60}
                value={meta.recruiterName || ""}
                placeholder="Jordan Lee"
                onChange={(e) => updateMeta({ recruiterName: e.target.value })}
              />
            </label>
            <label className="system-gen__field">
              <span>Brand name (optional)</span>
              <input
                type="text"
                maxLength={80}
                value={meta.companyName || ""}
                placeholder={business?.name || "Agency name"}
                onChange={(e) => updateMeta({ companyName: e.target.value })}
              />
            </label>
          </>
        ) : (
          <>
            <label className="system-gen__field">
              <span>{intakeMode ? "Programme name" : "Job title (invitation card)"}</span>
              <input
                type="text"
                maxLength={80}
                value={meta.jobTitle || ""}
                placeholder={intakeMode ? "Accounts / Finance Assistant Level 2" : "Senior Kitchen Manager"}
                onChange={(e) => updateMeta({ jobTitle: e.target.value })}
              />
            </label>
            <label className="system-gen__field">
              <span>{intakeMode ? "Provider name" : "Company name (invitation card)"}</span>
              <input
                type="text"
                maxLength={80}
                value={meta.companyName || ""}
                placeholder={business?.name || (intakeMode ? "Provider name" : "Company name")}
                onChange={(e) => updateMeta({ companyName: e.target.value })}
              />
            </label>
            <label className="system-gen__field">
              <span>{intakeMode ? "Delivery area" : "Location (invitation card)"}</span>
              <input
                type="text"
                maxLength={80}
                value={meta.location || ""}
                placeholder={intakeMode ? "England" : "Chicago, IL (On-site)"}
                onChange={(e) => updateMeta({ location: e.target.value })}
              />
            </label>
            <label className="system-gen__field">
              <span>{intakeMode ? "Reviewer / team name (optional)" : "Recruiter name (optional)"}</span>
              <input
                type="text"
                maxLength={60}
                value={meta.recruiterName || ""}
                placeholder={intakeMode ? "Partnerships team" : "Jordan Lee"}
                onChange={(e) => updateMeta({ recruiterName: e.target.value })}
              />
            </label>
            <label className="system-gen__field">
              <span>{intakeMode ? "Example respondent name (optional)" : "Candidate name (optional)"}</span>
              <input
                type="text"
                maxLength={60}
                value={meta.candidateName || ""}
                placeholder={intakeMode ? "Richard" : "John"}
                onChange={(e) => updateMeta({ candidateName: e.target.value })}
              />
            </label>
            <label className="system-gen__field">
              <span>Minimum required questions</span>
              <input
                type="number"
                min="2"
                max="10"
                value={minRequiredQuestions}
                onChange={(e) =>
                  updateMeta({
                    minRequiredQuestions: Math.max(2, Math.min(10, Number(e.target.value || (intakeMode ? 3 : 2)))),
                  })
                }
              />
            </label>
          </>
        )}
      </div>
      {isRecruitment && !isRec2Rec && (
        <div className="system-gen__panel">
          <div className="system-gen__panel-header">
            <div>
              <p className="system-gen__eyebrow">Contact capture</p>
              <h3>Contact details (optional)</h3>
            </div>
            <p className="system-gen__subtitle">
              Toggle contact collection and optionally prefill details for confirmation.
            </p>
          </div>
          <div className="system-gen__grid system-gen__grid--three">
            <label className="system-gen__field">
              <span>Require contact details</span>
              <div className="system-gen__toggle">
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={contactRequired}
                    onChange={(e) => updateMeta({ contactRequired: e.target.checked })}
                  />
                  <span className="toggle__label">
                    {contactRequired ? "Required" : "Optional"}
                  </span>
                </label>
              </div>
            </label>
            <label className="system-gen__field">
              <span>Require name only</span>
              <div className="system-gen__toggle">
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={nameRequired}
                    onChange={(e) => updateMeta({ nameRequired: e.target.checked })}
                  />
                  <span className="toggle__label">
                    {nameRequired ? "Required" : "Optional"}
                  </span>
                </label>
              </div>
            </label>
            <label className="system-gen__field">
              <span>Prefill name</span>
              <input
                type="text"
                maxLength={80}
                value={meta.contactPrefillName || ""}
                placeholder="Amitesh"
                onChange={(e) => updateMeta({ contactPrefillName: e.target.value })}
              />
            </label>
            <label className="system-gen__field">
              <span>Prefill email</span>
              <input
                type="text"
                maxLength={120}
                value={meta.contactPrefillEmail || ""}
                placeholder="name@email.com"
                onChange={(e) => updateMeta({ contactPrefillEmail: e.target.value })}
              />
            </label>
            <label className="system-gen__field">
              <span>Prefill phone</span>
              <input
                type="text"
                maxLength={40}
                value={meta.contactPrefillPhone || ""}
                placeholder="07000 000000"
                onChange={(e) => updateMeta({ contactPrefillPhone: e.target.value })}
              />
            </label>
            <label className="system-gen__field system-gen__field--wide">
              <span>Prefill message</span>
              <input
                type="text"
                maxLength={180}
                value={meta.contactPrefillMessage || ""}
                placeholder="We have the following details on file. Please confirm or correct them below."
                onChange={(e) => updateMeta({ contactPrefillMessage: e.target.value })}
              />
            </label>
          </div>
        </div>
      )}
      <div className="system-gen__grid system-gen__grid--three">
        <label className="system-gen__field">
          <span>Store eyebrow</span>
          <input
            type="text"
            maxLength={80}
            value={meta.storeEyebrow || ""}
            placeholder={isRec2Rec ? "Confidential profile" : "Storefront"}
            onChange={(e) => updateMeta({ storeEyebrow: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Hero eyebrow</span>
          <input
            type="text"
            maxLength={60}
            value={meta.heroEyebrow || meta.personality || ""}
            placeholder="Services that fit you"
            onChange={(e) => updateMeta({ heroEyebrow: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Hero title (also header title)</span>
          <input
            type="text"
            maxLength={80}
            value={meta.heroTitle || ""}
            placeholder={
              isRec2Rec
                ? "Confidential Rec2Rec pre-screen"
                : `Work with ${business?.name || "our team"}`
            }
            onChange={(e) => updateMeta({ heroTitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Primary CTA label</span>
          <input
            type="text"
            maxLength={40}
            value={meta.heroPrimaryCta || ""}
            placeholder="Browse offers"
            onChange={(e) => updateMeta({ heroPrimaryCta: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Secondary CTA label</span>
          <input
            type="text"
            maxLength={40}
            value={meta.heroSecondaryCta || ""}
            placeholder="Contact / Apply"
            onChange={(e) => updateMeta({ heroSecondaryCta: e.target.value })}
          />
        </label>
      </div>

      <div className="system-gen__grid system-gen__grid--three">
        <label className="system-gen__field">
          <span>Hero subtitle</span>
          <input
            type="text"
            maxLength={140}
            value={meta.heroSubtitle || ""}
            placeholder={
              isRec2Rec
                ? "Share a quick profile so we can match you to suitable agencies."
                : "Hybrid services, downloads, and bookings."
            }
            onChange={(e) => updateMeta({ heroSubtitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Catalog title</span>
          <input
            type="text"
            maxLength={80}
            value={meta.catalogTitle || ""}
            placeholder="Services, downloads, subscriptions"
            onChange={(e) => updateMeta({ catalogTitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Catalog subtitle</span>
          <input
            type="text"
            maxLength={140}
            value={meta.catalogSubtitle || ""}
            placeholder="Search, add to cart, and pay online or cash."
            onChange={(e) => updateMeta({ catalogSubtitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Catalog search placeholder</span>
          <input
            type="text"
            maxLength={80}
            value={meta.catalogSearchPlaceholder || ""}
            placeholder="Search services or products"
            onChange={(e) => updateMeta({ catalogSearchPlaceholder: e.target.value })}
          />
        </label>
      </div>

      <div className="system-gen__grid system-gen__grid--three">
        <label className="system-gen__field">
          <span>Lead/contact card title</span>
          <input
            type="text"
            maxLength={60}
            value={meta.ctaLeadTitle || ""}
            placeholder="Get in touch"
            onChange={(e) => updateMeta({ ctaLeadTitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Lead/contact description</span>
          <input
            type="text"
            maxLength={120}
            value={meta.ctaLeadSubtitle || ""}
            placeholder="Tell us what you need and we’ll match you."
            onChange={(e) => updateMeta({ ctaLeadSubtitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Lead/contact button</span>
          <input
            type="text"
            maxLength={40}
            value={meta.ctaLeadAction || ""}
            placeholder="Contact us"
            onChange={(e) => updateMeta({ ctaLeadAction: e.target.value })}
          />
        </label>

        <label className="system-gen__field">
          <span>Downloads card title</span>
          <input
            type="text"
            maxLength={60}
            value={meta.ctaProgramsTitle || ""}
            placeholder="Digital downloads"
            onChange={(e) => updateMeta({ ctaProgramsTitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Downloads description</span>
          <input
            type="text"
            maxLength={120}
            value={meta.ctaProgramsSubtitle || ""}
            placeholder="Guides and bundles to use anytime."
            onChange={(e) => updateMeta({ ctaProgramsSubtitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Downloads button</span>
          <input
            type="text"
            maxLength={40}
            value={meta.ctaProgramsAction || ""}
            placeholder="Browse downloads"
            onChange={(e) => updateMeta({ ctaProgramsAction: e.target.value })}
          />
        </label>

        <label className="system-gen__field">
          <span>Bookings/services card title</span>
          <input
            type="text"
            maxLength={60}
            value={meta.ctaBookTitle || ""}
            placeholder="Book services"
            onChange={(e) => updateMeta({ ctaBookTitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Bookings/services description</span>
          <input
            type="text"
            maxLength={120}
            value={meta.ctaBookSubtitle || ""}
            placeholder="Lock in time for in-person or virtual."
            onChange={(e) => updateMeta({ ctaBookSubtitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Bookings/services button</span>
          <input
            type="text"
            maxLength={40}
            value={meta.ctaBookAction || ""}
            placeholder="Book now"
            onChange={(e) => updateMeta({ ctaBookAction: e.target.value })}
          />
        </label>
      </div>

      <div className="system-gen__grid system-gen__grid--three">
        <label className="system-gen__field">
          <span>Booking section eyebrow</span>
          <input
            type="text"
            maxLength={30}
            value={meta.bookingSectionEyebrow || ""}
            placeholder="Services"
            onChange={(e) => updateMeta({ bookingSectionEyebrow: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Booking section title</span>
          <input
            type="text"
            maxLength={80}
            value={meta.bookingSectionTitle || ""}
            placeholder="Select a service or slot"
            onChange={(e) => updateMeta({ bookingSectionTitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Booking section subtitle</span>
          <input
            type="text"
            maxLength={140}
            value={meta.bookingSectionSubtitle || ""}
            placeholder="Pick a plan, then choose a date and time."
            onChange={(e) => updateMeta({ bookingSectionSubtitle: e.target.value })}
          />
        </label>

        <label className="system-gen__field">
          <span>Catalog eyebrow</span>
          <input
            type="text"
            maxLength={30}
            value={meta.catalogEyebrow || ""}
            placeholder="Shop"
            onChange={(e) => updateMeta({ catalogEyebrow: e.target.value })}
          />
        </label>
      </div>

      <div className="system-gen__grid system-gen__grid--three">
        <label className="system-gen__field">
          <span>Accent emoji</span>
          <input
            type="text"
            maxLength={8}
            value={meta.accentEmoji || ""}
            placeholder="⭐️"
            onChange={(e) => updateMeta({ accentEmoji: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Accent label</span>
          <input
            type="text"
            maxLength={40}
            value={meta.accentLabel || ""}
            placeholder="Signature"
            onChange={(e) => updateMeta({ accentLabel: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Accent colour (hex)</span>
          <input
            type="text"
            maxLength={16}
            value={meta.accentColor || ""}
            placeholder="#1f2937"
            onChange={(e) => updateMeta({ accentColor: e.target.value })}
          />
        </label>
      </div>

      <label className="system-gen__field system-gen__field--wide">
        <span>Quick answers (one per line)</span>
        <textarea
          rows={4}
          maxLength={480}
          value={quickAnswersText}
          placeholder={"Pricing shown before checkout\nChoose your location at checkout\nPay cash or card"}
          onChange={(e) => handleQuickAnswersChange(e.target.value)}
        />
      </label>

      <div className="system-gen__panel-divider" />

      <div className="system-gen__panel-header">
        <div>
          <p className="system-gen__eyebrow">Lead form</p>
          <h3>Contact/apply copy</h3>
        </div>
        <p className="system-gen__subtitle">AI can draft this; edit if needed.</p>
      </div>
      <div className="system-gen__grid system-gen__grid--three">
        <label className="system-gen__field">
          <span>Lead form eyebrow</span>
          <input
            type="text"
            maxLength={60}
            value={meta.leadFormEyebrow || ""}
            placeholder="Apply / Ask"
            onChange={(e) => updateMeta({ leadFormEyebrow: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Lead form title</span>
          <input
            type="text"
            maxLength={120}
            value={meta.leadFormTitle || ""}
            placeholder="Tell us your goal"
            onChange={(e) => updateMeta({ leadFormTitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Lead form subtitle</span>
          <input
            type="text"
            maxLength={200}
            value={meta.leadFormSubtitle || ""}
            placeholder="We’ll reply with the right plan and booking link."
            onChange={(e) => updateMeta({ leadFormSubtitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Submit button</span>
          <input
            type="text"
            maxLength={40}
            value={meta.leadFormSubmitLabel || ""}
            placeholder="Send"
            onChange={(e) => updateMeta({ leadFormSubmitLabel: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Name placeholder</span>
          <input
            type="text"
            maxLength={80}
            value={meta.leadFormNamePlaceholder || ""}
            placeholder="Your name"
            onChange={(e) => updateMeta({ leadFormNamePlaceholder: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Email placeholder</span>
          <input
            type="text"
            maxLength={80}
            value={meta.leadFormEmailPlaceholder || ""}
            placeholder="Email"
            onChange={(e) => updateMeta({ leadFormEmailPlaceholder: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Phone placeholder</span>
          <input
            type="text"
            maxLength={80}
            value={meta.leadFormPhonePlaceholder || ""}
            placeholder="Phone"
            onChange={(e) => updateMeta({ leadFormPhonePlaceholder: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Location placeholder</span>
          <input
            type="text"
            maxLength={120}
            value={meta.leadFormLocationPlaceholder || ""}
            placeholder="Preferred location (city/online)"
            onChange={(e) => updateMeta({ leadFormLocationPlaceholder: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Goal placeholder</span>
          <input
            type="text"
            maxLength={120}
            value={meta.leadFormGoalPlaceholder || ""}
            placeholder="What are you looking to achieve?"
            onChange={(e) => updateMeta({ leadFormGoalPlaceholder: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Background placeholder</span>
          <input
            type="text"
            maxLength={120}
            value={meta.leadFormExperiencePlaceholder || ""}
            placeholder="Background / current setup"
            onChange={(e) => updateMeta({ leadFormExperiencePlaceholder: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Details placeholder</span>
          <input
            type="text"
            maxLength={120}
            value={meta.leadFormWeightPlaceholder || ""}
            placeholder="Additional details (optional)"
            onChange={(e) => updateMeta({ leadFormWeightPlaceholder: e.target.value })}
          />
        </label>
        <label className="system-gen__field system-gen__field--wide">
          <span>Notes placeholder</span>
          <input
            type="text"
            maxLength={200}
            value={meta.leadFormNotesPlaceholder || ""}
            placeholder="Questions, timing, constraints"
            onChange={(e) => updateMeta({ leadFormNotesPlaceholder: e.target.value })}
          />
        </label>
      </div>
    </div>
  );
}

function RecruitmentMetaEditor({ blueprint, setBlueprint, business, rec2RecMode = false }) {
  const canEdit = Boolean(blueprint && setBlueprint);
  const meta = blueprint?.templateMeta || {};
  const isLocalService = isLocalServiceQuoteFlow(blueprint);
  const isIntake = meta.recruitmentMode === "intake" && !rec2RecMode;
  const contactRequired = Boolean(meta.contactRequired);
  const nameRequired = Boolean(meta.nameRequired);
  const defaultValidUntil = React.useMemo(() => defaultValidUntilIso(), []);

  const updateMeta = useCallback(
    (updates) => {
      if (!canEdit) return;
      setBlueprint((prev) => {
        if (!prev) return prev;
        return { ...prev, templateMeta: { ...(prev.templateMeta || {}), ...updates } };
      });
    },
    [canEdit, setBlueprint]
  );

  useEffect(() => {
    if (!canEdit) return;
    if (!meta.validUntil) {
      updateMeta({
        validUntil: defaultValidUntil,
        validUntilLabel: formatValidUntilLabel(defaultValidUntil),
      });
    }
  }, [canEdit, meta.validUntil, defaultValidUntil, updateMeta]);

  useEffect(() => {
    if (!canEdit) return;
    if (meta.recruitmentMode !== "intake") return;
    const currentLabel = meta.label || "";
    const currentHeroTitle = meta.heroTitle || "";
    const currentHeroSubtitle = meta.heroSubtitle || "";
    const currentMetaDescription = meta.metaDescription || "";
    const currentNextStepTitle = meta.nextStepTitle || "";
    const currentNextStepSubtitle = meta.nextStepSubtitle || "";
    const resets = {};
    const recruitmentDefaults = {
      label: "Recruitment Screening",
      heroTitle: "Apprenticeship programme",
      heroSubtitle: "For employers looking to hire or upskill staff.",
      metaDescription: "Employer enquiry form for apprenticeship-style programmes.",
      nextStepTitle: "Register your interest",
      nextStepSubtitle: "Tell us a few details and we'll be in touch.",
    };
    Object.entries(recruitmentDefaults).forEach(([key, value]) => {
      const currentValue = {
        label: currentLabel,
        heroTitle: currentHeroTitle,
        heroSubtitle: currentHeroSubtitle,
        metaDescription: currentMetaDescription,
        nextStepTitle: currentNextStepTitle,
        nextStepSubtitle: currentNextStepSubtitle,
      }[key];
      if ((currentValue || "").trim() === value) {
        resets[key] = "";
      }
    });
    if (Object.keys(resets).length) {
      updateMeta(resets);
    }
  }, [
    canEdit,
    meta.recruitmentMode,
    meta.label,
    meta.heroTitle,
    meta.heroSubtitle,
    meta.metaDescription,
    meta.nextStepTitle,
    meta.nextStepSubtitle,
    updateMeta,
  ]);

  if (!canEdit) return null;

  return (
    <div className="system-gen__panel">
      <div className="system-gen__panel-header">
        <div>
          <p className="system-gen__eyebrow">
            {isLocalService ? "Quote page copy" : isIntake ? "Intake copy" : "Recruitment copy"}
          </p>
          <h3>{isLocalService ? "Header and service details" : "Header and role details"}</h3>
        </div>
        <p className="system-gen__subtitle">
          {isLocalService
            ? "Control the reusable quote copy and service summary fields."
            : isIntake
            ? "Control the text shown on the intake page. Leave blank to keep AI defaults."
            : "Control the text shown on the recruitment page. Leave blank to keep AI defaults."}
        </p>
      </div>
      <div className="system-gen__grid system-gen__grid--three">
        <label className="system-gen__field">
          <span>{isLocalService ? "Business name" : "Company name"}</span>
          <input
            type="text"
            maxLength={80}
            value={meta.companyName || ""}
            placeholder={business?.name || "Company name"}
            onChange={(e) => updateMeta({ companyName: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Page label</span>
          <input
            type="text"
            maxLength={40}
            value={meta.label || ""}
            placeholder={isIntake ? "Intake" : "Recruitment"}
            onChange={(e) => updateMeta({ label: e.target.value })}
          />
        </label>
        {isLocalService && (
          <label className="system-gen__field">
            <span>Hero badge</span>
            <input
              type="text"
              maxLength={60}
              value={meta.serviceBadgeLabel || ""}
              placeholder="Guided quote request"
              onChange={(e) => updateMeta({ serviceBadgeLabel: e.target.value })}
            />
          </label>
        )}
        {!rec2RecMode && !isLocalService && (
          <label className="system-gen__field">
            <span>Mode</span>
            <select
              value={meta.recruitmentMode === "intake" ? "intake" : "standard"}
              onChange={(e) => updateMeta({ recruitmentMode: e.target.value })}
            >
              <option value="standard">Recruitment</option>
              <option value="intake">Intake</option>
            </select>
          </label>
        )}
        <label className="system-gen__field system-gen__field--wide">
          <span>Headline</span>
          <input
            type="text"
            maxLength={120}
              value={meta.heroTitle || ""}
              placeholder={
                isLocalService
                  ? "Request your carpet cleaning quote"
                  : isIntake
                  ? "Tell us about your apprenticeship enquiry"
                  : "Are you ready to take the next step in your career?"
              }
            onChange={(e) => updateMeta({ heroTitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field system-gen__field--wide">
          <span>Subheadline</span>
          <input
            type="text"
            maxLength={180}
              value={meta.heroSubtitle || ""}
              placeholder={
                isLocalService
                  ? "Answer a few quick questions so we can price the job properly and get back to you fast."
                  : isIntake
                  ? "Choose hire vs upskill vs learner enquiry so the team can follow up quickly."
                  : "Hi there — thanks for your interest in the role."
              }
            onChange={(e) => updateMeta({ heroSubtitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field system-gen__field--wide">
          <span>{isLocalService ? "Supporting line" : "Supporting line / intro"}</span>
          <input
            type="text"
            maxLength={180}
            value={meta.intro || ""}
            placeholder={
              isLocalService
                ? "Tell us what you need and we'll be in touch."
                : isIntake
                  ? "Tell us what you need and we'll be in touch."
                  : "Optional short supporting line."
            }
            onChange={(e) => updateMeta({ intro: e.target.value })}
          />
        </label>
        <label className="system-gen__field system-gen__field--wide">
          <span>Meta description</span>
          <input
            type="text"
            maxLength={200}
              value={meta.metaDescription || ""}
              placeholder={
                isLocalService
                  ? "Short quote request form for local carpet cleaning and upholstery enquiries."
                  : isIntake
                  ? "Short apprenticeship enquiry flow for employers and learners."
                  : "Confirm alignment details before a screening call."
              }
            onChange={(e) => updateMeta({ metaDescription: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Next step title</span>
          <input
            type="text"
            maxLength={80}
            value={meta.nextStepTitle || ""}
            placeholder={
              isLocalService
                ? "Tell us about the job"
                : isIntake
                  ? "Tell us about your enquiry"
                  : "Confirm a few details"
            }
            onChange={(e) => updateMeta({ nextStepTitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Next step eyebrow</span>
          <input
            type="text"
            maxLength={40}
            value={meta.nextStepEyebrow || ""}
            placeholder="Next step"
            onChange={(e) => updateMeta({ nextStepEyebrow: e.target.value })}
          />
        </label>
        <label className="system-gen__field system-gen__field--wide">
          <span>Next step subtitle</span>
          <input
            type="text"
            maxLength={160}
            value={meta.nextStepSubtitle || ""}
            placeholder={
              isLocalService
                ? "Share the cleaning details, your area, and how you'd like us to follow up."
                : isIntake
                  ? "This short intake helps us route your enquiry and follow up with the right next step."
                  : "This helps me confirm alignment before I schedule a full screening call with you."
            }
            onChange={(e) => updateMeta({ nextStepSubtitle: e.target.value })}
          />
        </label>
      </div>

      {!rec2RecMode && (
        <>
          <div className="system-gen__panel-divider" />
            <div className="system-gen__panel-header">
              <div>
              <p className="system-gen__eyebrow">
                {isLocalService ? "Quote summary" : isIntake ? "Programme card" : "Role card"}
              </p>
              <h3>
                {isLocalService
                  ? "Quote summary fields"
                  : isIntake
                    ? "Programme summary fields"
                    : "Role summary fields"}
              </h3>
              </div>
            </div>
          <div className="system-gen__grid system-gen__grid--three">
            <label className="system-gen__field">
              <span>{isLocalService ? "Service name" : isIntake ? "Programme name" : "Job title"}</span>
              <input
                type="text"
                maxLength={80}
                value={meta.jobTitle || ""}
                placeholder={isLocalService ? "Carpet cleaning" : isIntake ? "Programme name" : "Quality Inspector"}
                onChange={(e) => updateMeta({ jobTitle: e.target.value })}
              />
            </label>
            <label className="system-gen__field">
              <span>{isLocalService ? "Service area" : "Location"}</span>
              <input
                type="text"
                maxLength={80}
                value={meta.location || ""}
                placeholder="Cardiff, CF3"
                onChange={(e) => updateMeta({ location: e.target.value })}
              />
            </label>
            {!isLocalService && (
              <label className="system-gen__field">
                <span>{isIntake ? "Funding / fee" : "Salary range"}</span>
                <input
                  type="text"
                  maxLength={80}
                  value={meta.salaryRange || ""}
                  placeholder={isIntake ? "£1,200" : "£25,000 - £30,000"}
                  onChange={(e) => updateMeta({ salaryRange: e.target.value })}
                />
              </label>
            )}
            <label className="system-gen__field">
              <span>Valid until</span>
              <input
                type="date"
                value={meta.validUntil || defaultValidUntil}
                onChange={(e) =>
                  updateMeta({
                    validUntil: e.target.value,
                    validUntilLabel: formatValidUntilLabel(e.target.value),
                  })
                }
              />
            </label>
            <label className="system-gen__field">
              <span>{isLocalService ? "Business name override" : isIntake ? "Organisation name" : "Recruiter name"}</span>
              <input
                type="text"
                maxLength={80}
                value={meta.recruiterName || ""}
                placeholder={isLocalService ? business?.name || "Business name" : isIntake ? "Training provider" : "Sigma Recruitment Team"}
                onChange={(e) => updateMeta({ recruiterName: e.target.value })}
              />
            </label>
            {!isLocalService && (
              <label className="system-gen__field">
                <span>{isIntake ? "Programme type" : "Employment type"}</span>
                <input
                  type="text"
                  maxLength={60}
                  value={meta.employmentType || ""}
                  placeholder={isIntake ? "Training course" : "Full-time"}
                  onChange={(e) => updateMeta({ employmentType: e.target.value })}
                />
              </label>
            )}
            <label className="system-gen__field">
              <span>{isLocalService ? "Business summary" : "Department"}</span>
              <input
                type="text"
                maxLength={60}
                value={meta.department || ""}
                placeholder={isLocalService ? "Quick service summary" : isIntake ? "Learning team" : "Quality Assurance"}
                onChange={(e) => updateMeta({ department: e.target.value })}
              />
            </label>
            <label className="system-gen__field">
              <span>{isLocalService ? "Service label" : isIntake ? "Programme label" : "Role label"}</span>
              <input
                type="text"
                maxLength={40}
                value={meta.roleLabel || ""}
                placeholder={isLocalService ? "Service" : isIntake ? "Programme" : "Role"}
                onChange={(e) => updateMeta({ roleLabel: e.target.value })}
              />
            </label>
            <label className="system-gen__field">
              <span>{isLocalService ? "Business summary label" : isIntake ? "Funding label" : "Salary label"}</span>
              <input
                type="text"
                maxLength={40}
                value={meta.salaryLabel || ""}
                placeholder={isLocalService ? "Business summary" : isIntake ? "Funding / Fee" : "Salary range"}
                onChange={(e) => updateMeta({ salaryLabel: e.target.value })}
              />
            </label>
            <label className="system-gen__field">
              <span>{isLocalService ? "Business label" : "Organisation label"}</span>
              <input
                type="text"
                maxLength={40}
                value={meta.organisationLabel || ""}
                placeholder={isLocalService ? "Business" : isIntake ? "Organisation" : "Recruiter"}
                onChange={(e) => updateMeta({ organisationLabel: e.target.value })}
              />
            </label>
            <label className="system-gen__field">
              <span>{isLocalService ? "Service area label" : "Location label"}</span>
              <input
                type="text"
                maxLength={40}
                value={meta.locationLabel || ""}
                placeholder="Location"
                onChange={(e) => updateMeta({ locationLabel: e.target.value })}
              />
            </label>
            <label className="system-gen__field">
              <span>Valid until prefix</span>
              <input
                type="text"
                maxLength={30}
                value={meta.validUntilPrefix || ""}
                placeholder="Valid until"
                onChange={(e) => updateMeta({ validUntilPrefix: e.target.value })}
              />
            </label>
            <label className="system-gen__field system-gen__field--wide">
              <span>Time badge template</span>
              <input
                type="text"
                maxLength={40}
                value={meta.timeBadgeTemplate || ""}
                placeholder="Takes ~{minutes}"
                onChange={(e) => updateMeta({ timeBadgeTemplate: e.target.value })}
              />
            </label>
          </div>
        </>
      )}

      <div className="system-gen__panel-divider" />
      <div className="system-gen__panel-header">
        <div>
          <p className="system-gen__eyebrow">Contact capture</p>
          <h3>Contact details</h3>
        </div>
        <p className="system-gen__subtitle">
          {isLocalService
            ? "These labels and placeholders control the quote contact section."
            : isIntake
            ? "These labels and placeholders control the contact details section."
            : "These labels and placeholders control the candidate details section."}
        </p>
      </div>
      <div className="system-gen__grid system-gen__grid--three">
        <label className="system-gen__field">
          <span>Require contact details</span>
          <div className="system-gen__toggle">
            <label className="toggle">
              <input
                type="checkbox"
                checked={contactRequired}
                onChange={(e) => updateMeta({ contactRequired: e.target.checked })}
              />
              <span className="toggle__label">{contactRequired ? "Required" : "Optional"}</span>
            </label>
          </div>
        </label>
        <label className="system-gen__field">
          <span>Require name only</span>
          <div className="system-gen__toggle">
            <label className="toggle">
              <input
                type="checkbox"
                checked={nameRequired}
                onChange={(e) => updateMeta({ nameRequired: e.target.checked })}
              />
              <span className="toggle__label">{nameRequired ? "Required" : "Optional"}</span>
            </label>
          </div>
        </label>
        <label className="system-gen__field">
          <span>Section title</span>
          <input
            type="text"
            maxLength={80}
            value={meta.leadFormTitle || ""}
            placeholder="Your details"
            onChange={(e) => updateMeta({ leadFormTitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Section subtitle</span>
          <input
            type="text"
            maxLength={160}
            value={meta.leadFormSubtitle || ""}
            placeholder={
              isLocalService
                ? "Add the best details for the team to reply with a quote."
                : isIntake
                ? "Your answers are reviewed for eligibility, evidence, and next steps."
                : "Your answers are reviewed before arranging the call."
            }
            onChange={(e) => updateMeta({ leadFormSubtitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Submit button label</span>
          <input
            type="text"
            maxLength={60}
            value={meta.leadFormSubmitLabel || ""}
            placeholder={isLocalService ? "Request quote" : isIntake ? "Submit" : "Send details"}
            onChange={(e) => updateMeta({ leadFormSubmitLabel: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Name placeholder</span>
          <input
            type="text"
            maxLength={80}
            value={meta.leadFormNamePlaceholder || ""}
            placeholder="Full name"
            onChange={(e) => updateMeta({ leadFormNamePlaceholder: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Name label</span>
          <input
            type="text"
            maxLength={80}
            value={meta.leadFormNameLabel || ""}
            placeholder="Full name"
            onChange={(e) => updateMeta({ leadFormNameLabel: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Email placeholder</span>
          <input
            type="text"
            maxLength={80}
            value={meta.leadFormEmailPlaceholder || ""}
            placeholder="Email address"
            onChange={(e) => updateMeta({ leadFormEmailPlaceholder: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Email label</span>
          <input
            type="text"
            maxLength={80}
            value={meta.leadFormEmailLabel || ""}
            placeholder={isLocalService ? "Email address" : isIntake ? "Email address" : "Personal email"}
            onChange={(e) => updateMeta({ leadFormEmailLabel: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Phone placeholder</span>
          <input
            type="text"
            maxLength={80}
            value={meta.leadFormPhonePlaceholder || ""}
            placeholder="Phone number"
            onChange={(e) => updateMeta({ leadFormPhonePlaceholder: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Phone label</span>
          <input
            type="text"
            maxLength={80}
            value={meta.leadFormPhoneLabel || ""}
            placeholder="Mobile number"
            onChange={(e) => updateMeta({ leadFormPhoneLabel: e.target.value })}
          />
        </label>
        <label className="system-gen__field system-gen__field--wide">
          <span>Privacy note</span>
          <input
            type="text"
            maxLength={200}
            value={meta.leadFormDisclaimer || ""}
            placeholder={
              isLocalService
                ? "Your details are only used to respond to this quote request."
                : isIntake
                ? "Your details are only used for this apprenticeship enquiry."
                : "Your details are only used for this hiring process."
            }
            onChange={(e) => updateMeta({ leadFormDisclaimer: e.target.value })}
          />
        </label>
        <label className="system-gen__field system-gen__field--wide">
          <span>Review note</span>
          <input
            type="text"
            maxLength={200}
            value={meta.leadFormReviewNote || ""}
            placeholder={
              isLocalService
                ? "Your answers are reviewed so the team can confirm scope, timing, and next steps."
                : isIntake
                ? "Your answers are reviewed to confirm eligibility and next actions."
                : "Your answers are reviewed before arranging the call."
            }
            onChange={(e) => updateMeta({ leadFormReviewNote: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Select placeholder</span>
          <input
            type="text"
            maxLength={40}
            value={meta.selectPlaceholder || ""}
            placeholder="Select"
            onChange={(e) => updateMeta({ selectPlaceholder: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Section fallback title</span>
          <input
            type="text"
            maxLength={60}
            value={meta.sectionFallbackTitle || ""}
            placeholder="Section"
            onChange={(e) => updateMeta({ sectionFallbackTitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Question fallback label</span>
          <input
            type="text"
            maxLength={60}
            value={meta.questionFallbackLabel || ""}
            placeholder="Question"
            onChange={(e) => updateMeta({ questionFallbackLabel: e.target.value })}
          />
        </label>
        <label className="system-gen__field">
          <span>Short answer placeholder</span>
          <input
            type="text"
            maxLength={60}
            value={meta.shortAnswerPlaceholder || ""}
            placeholder="Short answer"
            onChange={(e) => updateMeta({ shortAnswerPlaceholder: e.target.value })}
          />
        </label>
        <label className="system-gen__field system-gen__field--wide">
          <span>Empty state copy</span>
          <input
            type="text"
            maxLength={160}
            value={meta.emptyStateCopy || ""}
            placeholder="Questions will appear here once configured."
            onChange={(e) => updateMeta({ emptyStateCopy: e.target.value })}
          />
        </label>
      </div>

      <div className="system-gen__panel-divider" />
      <div className="system-gen__panel-header">
        <div>
          <p className="system-gen__eyebrow">Confirmation</p>
          <h3>Post-submit message</h3>
        </div>
      </div>
      <div className="system-gen__grid system-gen__grid--three">
        <label className="system-gen__field system-gen__field--wide">
          <span>Confirmation title</span>
          <input
            type="text"
            maxLength={120}
            value={meta.confirmationTitle || ""}
            placeholder="Thanks — we’ve got your details."
            onChange={(e) => updateMeta({ confirmationTitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field system-gen__field--wide">
          <span>Confirmation subtitle</span>
          <input
            type="text"
            maxLength={200}
            value={meta.confirmationSubtitle || ""}
            placeholder="We’ll review this and confirm the next step."
            onChange={(e) => updateMeta({ confirmationSubtitle: e.target.value })}
          />
        </label>
        <label className="system-gen__field system-gen__field--wide">
          <span>Confirmation next steps</span>
          <input
            type="text"
            maxLength={200}
            value={meta.confirmationNextSteps || ""}
            placeholder="No further action is needed right now."
            onChange={(e) => updateMeta({ confirmationNextSteps: e.target.value })}
          />
        </label>
        <label className="system-gen__field system-gen__field--wide">
          <span>Confirmation secondary line</span>
          <input
            type="text"
            maxLength={200}
            value={meta.confirmationSecondary || ""}
            placeholder="We’ll be in touch shortly."
            onChange={(e) => updateMeta({ confirmationSecondary: e.target.value })}
          />
        </label>
      </div>
    </div>
  );
}

function SystemGeneratorPage({
  business = null,
  systemTypeOverride = null,
  rec2recDefault = null,
  intakeDefault = null,
  apprenticeshipEnquiryFlowOverride = null,
  activeTabOverride = null,
  hideAdvancedOptions = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { type: systemTypeParam } = useParams();
  const isSystemsHome = location.pathname === "/systems";
  const isRec2RecPath = systemTypeParam === "rec2rec";
  const systemType =
    systemTypeOverride || (systemTypeParam === "restaurant" ? "restaurant" : "recruitment");
  const activeTab = isSystemsHome
    ? "home"
    : activeTabOverride || (isRec2RecPath ? "rec2rec" : systemType);
  const isRecruitmentPath = systemType === "recruitment";
  const [prompt, setPrompt] = useState("");
  const [includeSettings, setIncludeSettings] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [menuPublishesState, setMenuPublishesState] = useState(
    business?.menuPublishes || { active: [], archived: [] }
  );
  const fallbackProfiles = [
    {
      key: "auto",
      label: "Auto-detect",
      hint: "Let AI infer a profile (barber, spa, golf, fitness, meetings).",
      prompt: "",
    },
    {
      key: "barber",
      label: "Barber",
      hint: "15–30 minute services, 10:00–19:00, capacity 1.",
      prompt:
        "If not specified, use barber defaults: 15-30 minute services, 10:00-19:00, capacity 1.",
    },
    {
      key: "spa",
      label: "Spa / Treatments",
      hint: "30/60/90 minutes, 10:00–20:00, capacity 1.",
      prompt:
        "If not specified, use spa defaults: 30/60/90 minute treatments, 10:00-20:00, capacity 1.",
    },
    {
      key: "golf",
      label: "Golf simulator",
      hint: "60 minutes, 11:00–22:00, up to 4 players.",
      prompt:
        "If not specified, use golf simulator defaults: 60-minute bays, 11:00-22:00, capacity up to 4 players per bay.",
    },
    {
      key: "fitness",
      label: "Fitness / Classes",
      hint: "45–60 minutes, 07:00 / 12:00 / 18:00, cap 12.",
      prompt:
        "If not specified, use fitness class defaults: 45-60 minute sessions at 07:00, 12:00, 18:00, capacity 12.",
    },
    {
      key: "meeting",
      label: "Meeting rooms",
      hint: "60 minutes, 09:00–18:00, cap 1.",
      prompt:
        "If not specified, use meeting room defaults: 60-minute bookings, 09:00-18:00, capacity 1.",
    },
  ];
  const [profileKey, setProfileKey] = useState("auto");
  const [isBusy, setIsBusy] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [error, setError] = useState("");
  const [publishError, setPublishError] = useState("");
  const [blueprint, setBlueprint] = useState(null);
  const [publishResult, setPublishResult] = useState(null);
  const [copyStatus, setCopyStatus] = useState("");
  const [aiStatus, setAiStatus] = useState("");
  const [aiError, setAiError] = useState("");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [availabilityApiUrl, setAvailabilityApiUrl] = useState("");
  const [bookingSettingsInput, setBookingSettingsInput] = useState({
    resourceLabel: "",
    slotDurationMinutes: 60,
    maxSimultaneousBookings: 1,
    notes: "",
  });
  const menuPublishes = menuPublishesState || { active: [], archived: [] };
  const planKey = (business?.planType || business?.plan || "free").toLowerCase();
  const planPublishLimit = React.useMemo(() => {
    const limits = {
      free: 1,
      starter: 3,
      enterprise: 100,
      lifetime_studio: 10,
      lifetime_agency: 25,
      lifetime_enterprise: 50,
    };
    return limits[planKey] || limits.free;
  }, [planKey]);
  const dailyPublishLimit = React.useMemo(() => {
    const limits = {
      free: 3,
      starter: 10,
      enterprise: null,
      lifetime_studio: 50,
      lifetime_agency: 50,
      lifetime_enterprise: 50,
    };
    return limits[planKey] ?? limits.free;
  }, [planKey]);
  const activePublishCount = Array.isArray(menuPublishes.active) ? menuPublishes.active.length : 0;
  const publishedNow = Boolean(publishResult?.publicUrl);
  const effectiveActiveCount = activePublishCount + (publishedNow ? 1 : 0);
  const atPublishLimit = effectiveActiveCount >= planPublishLimit;
  const todayIso = React.useMemo(() => new Date().toISOString().slice(0, 10), []);
  const todayPublishCount = React.useMemo(() => {
    const all = [...(menuPublishes.active || []), ...(menuPublishes.archived || [])];
    return all.filter((p) => (p.publishedAt || "").startsWith(todayIso)).length;
  }, [menuPublishes.active, menuPublishes.archived, todayIso]);
  const atDailyPublishLimit =
    dailyPublishLimit != null && todayPublishCount >= dailyPublishLimit;
  const publishUsage = React.useMemo(() => {
    return {
      monthlyRemaining: Math.max(planPublishLimit - effectiveActiveCount, 0),
      monthlyLimit: planPublishLimit,
      resetsAt: null,
      subtitle: `${effectiveActiveCount} published / ${planPublishLimit} limit`,
    };
  }, [effectiveActiveCount, planPublishLimit]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [logoUploadUrl, setLogoUploadUrl] = useState(business?.logoUrl || "");
  const [pdfUploadUrl, setPdfUploadUrl] = useState(
    business?.digitalProductUrl || business?.pdfUrl || ""
  );
  const [pdfViewUrl, setPdfViewUrl] = useState("");
  const [imageUploadUrls, setImageUploadUrls] = useState([]);
  const [assetBusy, setAssetBusy] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [rec2RecMode, setRec2RecMode] = useState(
    rec2recDefault != null ? rec2recDefault : isRec2RecPath
  );
  const [intakeMode, setIntakeMode] = useState(
    intakeDefault != null ? intakeDefault : false
  );
  const [apprenticeshipEnquiryFlow, setApprenticeshipEnquiryFlow] = useState(
    Boolean(apprenticeshipEnquiryFlowOverride)
  );
  const logoInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [defaultRecruitmentSections, setDefaultRecruitmentSections] = useState(null);
  const asyncPollRef = useRef(null);
  const isLocalServiceQuote = isLocalServiceQuoteFlow(blueprint);
  const trackedQuoteLinks = React.useMemo(
    () =>
      isLocalServiceQuote && publishResult?.publicUrl
        ? buildTrackedQuoteLinks(publishResult.publicUrl, business?.name)
        : [],
    [business?.name, isLocalServiceQuote, publishResult?.publicUrl]
  );

  useEffect(() => {
    if (rec2RecMode && intakeMode) {
      setIntakeMode(false);
    }
  }, [rec2RecMode, intakeMode]);

  useEffect(() => {
    if (intakeMode && rec2RecMode) {
      setRec2RecMode(false);
    }
  }, [intakeMode, rec2RecMode]);

  useEffect(() => {
    if (apprenticeshipEnquiryFlow && !intakeMode) {
      setIntakeMode(true);
    }
    if (apprenticeshipEnquiryFlow && rec2RecMode) {
      setRec2RecMode(false);
    }
  }, [apprenticeshipEnquiryFlow, intakeMode, rec2RecMode]);

  useEffect(() => {
    if ((systemType || "").toLowerCase() !== "recruitment") return;
    if (apprenticeshipEnquiryFlowOverride == null) return;
    setApprenticeshipEnquiryFlow(Boolean(apprenticeshipEnquiryFlowOverride));
  }, [apprenticeshipEnquiryFlowOverride, systemType]);

  useEffect(() => {
    if ((systemType || "").toLowerCase() !== "recruitment") return;
    if (apprenticeshipEnquiryFlowOverride != null) return;
    const mode = (blueprint?.templateMeta?.recruitmentMode || "").toLowerCase();
    const flowType = (blueprint?.templateMeta?.flowType || "").toLowerCase();
    setApprenticeshipEnquiryFlow(flowType === "apprenticeship_enquiry");
    if (mode === "intake") {
      setIntakeMode(true);
      return;
    }
    if (mode && mode !== "rec2rec") {
      setIntakeMode(false);
    }
  }, [
    apprenticeshipEnquiryFlowOverride,
    systemType,
    blueprint?.templateMeta?.flowType,
    blueprint?.templateMeta?.recruitmentMode,
  ]);

  useEffect(() => {
    if ((systemType || "").toLowerCase() !== "recruitment") return;
    if (!intakeMode) return;
    setBlueprint((prev) => {
      if (!prev) return prev;
      const currentMeta = prev.templateMeta || {};
      const nextMeta = { ...currentMeta };
      let changed = false;

      if ((currentMeta.recruitmentMode || "").toLowerCase() !== "intake") {
        nextMeta.recruitmentMode = "intake";
        changed = true;
      }
      if (currentMeta.contactRequired == null) {
        nextMeta.contactRequired = true;
        changed = true;
      }
      if (currentMeta.nameRequired == null) {
        nextMeta.nameRequired = true;
        changed = true;
      }
      if (currentMeta.minRequiredQuestions == null) {
        nextMeta.minRequiredQuestions = 3;
        changed = true;
      }

      if (!changed) return prev;
      return { ...prev, templateMeta: nextMeta };
    });
  }, [systemType, intakeMode, setBlueprint]);

  const copyToClipboard = useCallback(async (text) => {
    if (!text) return false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (err) {
      console.warn("Clipboard API failed, falling back to textarea copy.", err);
    }
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      const success = document.execCommand("copy");
      document.body.removeChild(textarea);
      return success;
    } catch (err) {
      console.warn("Fallback clipboard copy failed.", err);
      return false;
    }
  }, []);

  const handleCopyLink = useCallback(
    async (text) => {
      const success = await copyToClipboard(text);
      setCopyStatus(success ? "Copied" : "Copy failed");
      setTimeout(() => setCopyStatus(""), 2200);
      return success;
    },
    [copyToClipboard]
  );

  const settingsSummary = buildSettingsSummary(business);
  const recruitmentModeState = rec2RecMode ? "rec2rec" : intakeMode ? "intake" : "standard";
  const copy = React.useMemo(() => {
    if (systemType === "restaurant") {
      return {
        title: "Restaurant Ordering Builder",
        subtitle:
          "Describe the restaurant ordering experience you want. Layerbound turns plain words into a storefront you can review and publish.",
        placeholder:
          "Describe your restaurant, menu highlights, ordering flow, and hours. Include cuisine style, service model, and any pickup/delivery details.",
        emptyError: "Please describe the ordering system and menu details.",
        submitLabel: "Generate ordering page",
        promptHelp: "Include menu items, service model, hours, and any add-ons or combos.",
        quickPrompts: [
          "Fast-casual burger spot with 12 core items, add-ons, and family bundles. Lunch rush 11–3, dinner 5–9. Pickup + delivery.",
          "Upscale sushi bar, Omakase tasting, a la carte rolls, sake pairing add-ons. Reservations encouraged, curbside pickup.",
          "Coffee + bakery with pastries, sandwiches, seasonal drinks. Morning-first ordering, easy re-order, in-store pickup.",
        ],
      };
    }
    if (isSystemsHome) {
      return {
        title: "System Builder",
        subtitle:
          "Describe the booking or ordering system you want. Layerbound turns plain words into a storefront you can review and publish.",
        placeholder:
          "Describe the system you want to create. Include what you sell or book, hours, pricing, and any must-have details.",
        emptyError: "Please describe the system you want to generate.",
        submitLabel: "Generate system",
        promptHelp: "Include the core flow, hours, pricing, and any key requirements.",
        quickPrompts: [
          "Neighborhood cafe with brunch specials, online ordering, and pickup windows. Add-ons for coffee and sides.",
          "Fitness studio with class booking, membership options, and drop-in pricing. Show weekly schedule.",
          "Recruitment screen for kitchen manager role, on-site Chicago, 5+ years, salary range listed.",
        ],
      };
    }
    if (systemType === "recruitment" && rec2RecMode) {
      return {
        title: "Confidential Recruiter Profile Builder",
        subtitle:
          "Describe the confidential recruiter profile you want. Layerbound turns plain words into a short, shareable Rec2Rec flow.",
        placeholder:
          "Describe the profile you want to collect. Keep it conversational (e.g. 180/270/360, sector, perm/contract split, billings, move readiness, base salary, notice period).",
        emptyError: "Please describe the confidential profile you want to generate.",
        submitLabel: "Generate profile page",
        promptHelp: "Include desk snapshot, billings, move readiness, and move criteria.",
        quickPrompts: [
          "Rec2Rec profile for UK recruiters. Capture 180/270/360, sector, perm/contract split, billings, move readiness, base salary, and notice period.",
          "Confidential recruiter profile for tech market. Ask for sector, years in recruitment, billings last 12 months, move readiness, and relocation openness.",
          "Quick pre-qualification: desk snapshot, billings, desired base salary, notice period, and remote/hybrid preference.",
        ],
      };
    }
    if (systemType === "recruitment" && isLocalServiceQuote) {
      return {
        title: "Create Carpet-Cleaning Quote Link",
        subtitle:
          "Create a reusable carpet-cleaning quote page, then publish a shareable public link.",
        placeholder:
          "Optional: describe any business-specific service wording or follow-up context you want to add before publishing.",
        emptyError: "Use the local service quote template or describe the quote flow you want to generate.",
        submitLabel: "Generate quote page",
        promptHelp:
          "Use the checked-in quote template button for the preset flow, or generate a custom draft from a prompt.",
        quickPrompts: [
          "Carpet cleaning quote page for a local operator. Keep the questions focused on rooms, stains, postcode, timing, and preferred follow-up method.",
          "Sofa and upholstery cleaning quote flow. Ask for item count, fabric concerns, postcode, timing, and whether photos can be shared later.",
          "Short local cleaning quote page with a clear contact step and a neutral confirmation message.",
        ],
      };
    }
    if (systemType === "recruitment" && intakeMode) {
      return {
        title: "Create Apprenticeship Intake Link",
        subtitle:
          "For training providers and colleges: branded employer / learner enquiry links with clearer follow-up in seconds.",
        placeholder:
          "Describe the programme and the short enquiry flow you want (hire a new apprentice, upskill current staff, or learner enquiry, plus start timing and preferred contact method).",
        emptyError: "Please describe the apprenticeship intake flow.",
        submitLabel: "Generate intake link",
        promptHelp:
          "Include the programme name, employer / learner split, short follow-up questions, and what the team should see after submission.",
        quickPrompts: [
          "Accounts / Finance Assistant Level 2 enquiry flow for training provider. First ask whether they want to hire a new apprentice, upskill existing staff, or enquire as a learner. Then collect company name if employer, preferred contact method, start timing, and brief notes.",
          "Employer-led apprenticeship enquiry link for Business Admin. Keep it short: enquiry route, company name, number of apprentices, start timing, and preferred follow-up method.",
          "Training provider first-contact form for apprenticeship leads. Prioritize hire vs upskill vs learner routing and only ask for details that help the team follow up quickly.",
        ],
      };
    }
    if (systemType === "recruitment" && !rec2RecMode) {
      return {
        title: "Create Screening Link",
        subtitle:
          "Describe the role and what you want candidates to confirm before a call. We'll generate a short screening page you can share.",
        placeholder:
          "Describe the role and key details to confirm (e.g. location, salary range, start date, reporting line, and experience requirements).",
        emptyError: "Please describe the role and screening details.",
        submitLabel: "Generate screening link",
        promptHelp:
          "Include the role overview and the main questions you want candidates to answer.",
        quickPrompts: [
          "Platform Engineer for a fintech team. AWS, Terraform, CI/CD. Hybrid in London. Salary £60–75k. Start within 1–2 months.",
          "Account Executive for B2B SaaS. Outbound + inbound sales. Remote UK. £70k OTE. HubSpot experience preferred. Start next quarter.",
          "Quality inspector for manufacturing site. On-site near Cardiff. Factory QA experience required. Fixed-term contract. Immediate start.",
        ],
      };
    }
    return {
      title: "System Builder",
      subtitle:
        "Describe the booking or ordering system you want. Layerbound turns plain words into a storefront, review and publish.",
      placeholder:
        "Describe the system you want to create. Include what you sell or book, hours, pricing, and any must-have details.",
      emptyError: "Please describe the system you want to generate.",
      submitLabel: "Generate system",
      promptHelp: "Include the core flow, hours, pricing, and any key requirements.",
      quickPrompts: [
        "Neighborhood cafe with brunch specials, online ordering, and pickup windows. Add-ons for coffee and sides.",
        "Fitness studio with class booking, membership options, and drop-in pricing. Show weekly schedule.",
        "Recruitment screen for kitchen manager role, on-site Chicago, 5+ years, salary range listed.",
      ],
    };
  }, [isLocalServiceQuote, isSystemsHome, systemType, rec2RecMode, intakeMode]);

  const getAuthToken = useCallback(async () => {
    const { tokens } = await fetchAuthSession();
    const bearer = resolveAuthToken(tokens);
    if (!bearer) throw new Error("Session expired. Please sign in again.");
    return bearer;
  }, []);

  const cleanFileName = (name = "") => name.replace(/[^a-zA-Z0-9._-]/g, "-");
  const cloneJson = (value) => JSON.parse(JSON.stringify(value));

  const persistAssets = useCallback(
    async ({ logoUrl: nextLogoUrl, pdfUrl: nextPdfUrl }) => {
      if (!business?.businessId) return;
      const token = await getAuthToken();
      const address = business.businessAddress || {};
      const formData = new FormData();
      formData.append("businessId", business.businessId);
      formData.append("name", business.name || "");
      formData.append("email", business.email || "");
      formData.append("phone", business.phone || "");
      formData.append("logoUrl", nextLogoUrl ?? business.logoUrl ?? "");
      formData.append("openingHours", JSON.stringify(business.openingHours || {}));
      formData.append("timeZone", business.timeZone || "Europe/London");
      formData.append("primaryColor", business.primaryColor || "#2563eb");
      formData.append("secondaryColor", business.secondaryColor || "#1e3a8a");
      formData.append("businessFont", business.businessFont || "Montserrat");
      formData.append("addressLine1", (address.street || address.addressLine1 || "").trim());
      formData.append("addressLine2", (address.line2 || address.addressLine2 || "").trim());
      formData.append("addressCity", (address.city || "").trim());
      formData.append("addressPostcode", (address.postcode || address.zip || "").trim());
      formData.append("addressCountry", (address.country || "United Kingdom").trim());
      formData.append("currencyCode", (business.currencyCode || "GBP").trim());
      const resolvedPdfUrl = nextPdfUrl ?? business.digitalProductUrl ?? business.pdfUrl ?? "";
      formData.append("digitalProductUrl", resolvedPdfUrl);
      formData.append("pdfUrl", resolvedPdfUrl);
      formData.append("planType", business.plan || business.planType || "free");
      formData.append("onboardingComplete", business?.onboardingComplete === false ? "false" : "true");

      const res = await fetch(BUSINESS_API_URL, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "Failed to save assets");
      }
    },
    [business, getAuthToken]
  );

  const fetchPdfViewUrl = useCallback(
    async (fileNameOverride) => {
      if (!business?.businessId || !(pdfUploadUrl || fileNameOverride)) return;
      const fileName = fileNameOverride || decodeURIComponent((pdfUploadUrl.split("/").pop() || "").split("?")[0]);
      if (!fileName) return;
      try {
        const token = await getAuthToken();
        const res = await fetch(
          `${PRESIGN_API}?businessId=${business.businessId}&fileName=${encodeURIComponent(
            fileName
          )}&contentType=${encodeURIComponent("application/pdf")}&mode=get`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (res.ok) {
          const data = await res.json();
          if (data.getUrl) {
            setPdfViewUrl(data.getUrl);
          }
        }
      } catch (err) {
        console.warn("PDF view presign error:", err);
      }
    },
    [business?.businessId, getAuthToken, pdfUploadUrl]
  );

  const uploadAsset = useCallback(
    async (file, kind) => {
      if (!business?.businessId || !file) return;
      const isPdf = kind === "pdf";
      const isImage = kind === "image";
      const friendlyLabel = isPdf ? "PDF" : "logo";
      if (file.size > MAX_ASSET_SIZE) {
        setError(`${friendlyLabel} must be smaller than 200 KB.`);
        return;
      }
      if (isPdf) {
        const isPdfType =
          file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
        if (!isPdfType) {
          setError("Please upload a PDF file.");
          return;
        }
      } else if (isImage) {
        if (!(file.type || "").startsWith("image/")) {
          setError("Please upload an image file.");
          return;
        }
        if (imageUploadUrls.length >= MAX_IMAGE_COUNT) {
          setError(`You can upload up to ${MAX_IMAGE_COUNT} images.`);
          return;
        }
      } else if (!(file.type || "").startsWith("image/")) {
        setError("Please upload an image file for the logo.");
        return;
      }
      setAssetBusy(true);
      setError("");
      try {
        const token = await getAuthToken();
        const safeName = cleanFileName(file.name);
        const key = `${isPdf ? "digital" : isImage ? "images" : "logos"}/${safeName}`;
        const contentType = file.type || (isPdf ? "application/pdf" : "application/octet-stream");
        const res = await fetch(
          `${PRESIGN_API}?businessId=${business.businessId}&fileName=${encodeURIComponent(
            key
          )}&contentType=${encodeURIComponent(contentType)}`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!res.ok) {
          const errorText = await res.text().catch(() => "");
          throw new Error(errorText || "Failed to get presigned URL");
        }
        const { uploadUrl, publicUrl, getUrl } = await res.json();
        if (!uploadUrl) {
          throw new Error("Presign response missing uploadUrl");
        }
        const uploadRes = await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": contentType },
          body: file,
        });
        if (!uploadRes.ok) {
          throw new Error(`Failed to upload ${friendlyLabel} (${uploadRes.status})`);
        }
        if (isPdf) {
          setPdfUploadUrl(publicUrl);
          if (getUrl) setPdfViewUrl(getUrl);
          await persistAssets({ pdfUrl: publicUrl });
          if (!getUrl) {
            fetchPdfViewUrl(safeName);
          }
        } else if (isImage) {
          setImageUploadUrls((prev) => {
            const next = [...prev, publicUrl].slice(0, MAX_IMAGE_COUNT);
            return next;
          });
        } else {
          setLogoUploadUrl(publicUrl);
          await persistAssets({ logoUrl: publicUrl });
        }
        setShowAttachMenu(false);
        return publicUrl;
      } catch (err) {
        console.error("Asset upload failed:", err);
        setError(err?.message || "Could not upload file.");
      } finally {
        setAssetBusy(false);
      }
    },
    [business?.businessId, fetchPdfViewUrl, getAuthToken, imageUploadUrls.length, persistAssets]
  );

  useEffect(() => {
    setMenuPublishesState(business?.menuPublishes || { active: [], archived: [] });
  }, [business?.businessId, business?.menuPublishes, setMenuPublishesState]);

  useEffect(() => {
    setLogoUploadUrl(business?.logoUrl || "");
    const pdfUrl = business?.digitalProductUrl || business?.pdfUrl || "";
    setPdfUploadUrl(pdfUrl);
    setPdfViewUrl("");
    setImageUploadUrls([]);
  }, [business?.logoUrl, business?.digitalProductUrl, business?.pdfUrl]);

  useEffect(() => {
    if (pdfUploadUrl && business?.businessId) {
      fetchPdfViewUrl();
    }
  }, [pdfUploadUrl, business?.businessId, fetchPdfViewUrl]);

  useEffect(() => {
    if (!imageUploadUrls.length) return;
    setBlueprint((prev) => {
      if (!prev) return prev;
      const isDefaultMenu = ["default", "menu"].includes((prev.template || "").toLowerCase());
      const cappedImages = imageUploadUrls.slice(0, MAX_IMAGE_COUNT);
      const updatedItems =
        isDefaultMenu && cappedImages.length
          ? (prev.items || []).map((item, idx) =>
              idx < cappedImages.length
                ? { ...item, imageUrl: cappedImages[idx], image: cappedImages[idx] }
                : item
            )
          : prev.items || [];
      const updatedMeta = {
        ...(prev.templateMeta || {}),
        galleryImages: cappedImages,
        minRequiredQuestions: prev.templateMeta?.minRequiredQuestions ?? 2,
      };
      const templateName = (prev.template || "").toLowerCase();
      if (templateName === "coaching") {
        updatedMeta.coachingImages = cappedImages;
      }
      if (templateName === "ecommerce" || templateName === "commerce") {
        updatedMeta.ecommerceImages = cappedImages;
      }
      return {
        ...prev,
        items: updatedItems,
        templateMeta: updatedMeta,
        imageUrls: cappedImages,
      };
    });
  }, [imageUploadUrls]);

  const fetchPublishes = React.useCallback(async () => {
    if (!business?.businessId) return;
    try {
      const res = await fetch(`${API_URL}/${encodeURIComponent(business.businessId)}`);
      if (!res.ok) {
        console.warn("Failed to refresh publishes", res.status);
        return;
      }
      const data = await res.json();
      setAiStatus(data.aiStatus || "");
      setAiError(data.aiError || "");
      if (data?.publishes) {
        setMenuPublishesState(data.publishes);
      }
    } catch (err) {
      console.warn("Error refreshing publishes", err);
    }
  }, [business?.businessId]);

  const handleLogoSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadAsset(file, "logo");
    }
    if (event.target) {
      event.target.value = "";
    }
  };

  const handlePdfSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadAsset(file, "pdf");
    }
    if (event.target) {
      event.target.value = "";
    }
  };

  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    const remaining = Math.max(0, MAX_IMAGE_COUNT - imageUploadUrls.length);
    const toUpload = files.slice(0, remaining);
    if (remaining === 0) {
      setError(`You can upload up to ${MAX_IMAGE_COUNT} images.`);
    } else if (files.length > remaining) {
      setError(`Only ${remaining} more image${remaining === 1 ? "" : "s"} allowed (max ${MAX_IMAGE_COUNT}).`);
    } else {
      setError(null);
    }
    (async () => {
      for (const f of toUpload) {
        await uploadAsset(f, "image");
      }
    })();
    if (event.target) {
      event.target.value = "";
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = prompt.trim();
    if (!business?.businessId) {
      setError("Your business must be onboarded before generating systems.");
      return;
    }
    if (!trimmed) {
      setError(copy.emptyError);
      return;
    }
    if (atPublishLimit) {
      setError("You already have a live system. Archive or delete it before generating a new one on Free.");
      return;
    }
    setIsBusy(true);
    setError("");
    setPublishError("");
    setAiStatus("");
    setAiError("");
    if (asyncPollRef.current) {
      clearTimeout(asyncPollRef.current);
      asyncPollRef.current = null;
    }

    const applyGeneratedBlueprint = (data) => {
      const inferredTemplate = data.template || "default";
      const baseMeta = data && typeof data.templateMeta === "object" ? data.templateMeta || {} : {};
      const normalizedMeta = {
        ...baseMeta,
        label: baseMeta.label || data.template || "Menu catalog",
        kind: baseMeta.kind || data.blueprintKind || "menu",
        supportedPayments: baseMeta.supportedPayments || baseMeta.supported_payments || [],
      };
      const sanitizeRecruitmentMeta = (meta, mode) => {
        const allowed = new Set([
          "label",
          "kind",
          "supportedPayments",
          "supported_payments",
          "heroTitle",
          "heroSubtitle",
          "intro",
          "serviceBadgeLabel",
          "nextStepEyebrow",
          "nextStepTitle",
          "nextStepSubtitle",
          "metaDescription",
          "jobTitle",
          "roleTitle",
          "companyName",
          "candidateName",
          "recruiterName",
          "location",
          "employmentType",
          "salaryRange",
          "validUntil",
          "validUntilLabel",
          "department",
          "roleLabel",
          "salaryLabel",
          "organisationLabel",
          "locationLabel",
          "selectPlaceholder",
          "sectionFallbackTitle",
          "questionFallbackLabel",
          "shortAnswerPlaceholder",
          "validUntilPrefix",
          "timeBadgeTemplate",
          "flowType",
          "contactRequired",
          "nameRequired",
          "contactPrefillName",
          "contactPrefillEmail",
          "contactPrefillPhone",
          "contactPrefillMessage",
          "leadFormTitle",
          "leadFormSubtitle",
          "leadFormSubmitLabel",
          "leadFormNameLabel",
          "leadFormNamePlaceholder",
          "leadFormEmailLabel",
          "leadFormEmailPlaceholder",
          "leadFormPhoneLabel",
          "leadFormPhonePlaceholder",
          "leadFormDisclaimer",
          "leadFormReviewNote",
          "confirmationTitle",
          "confirmationSubtitle",
          "confirmationNextSteps",
          "confirmationSecondary",
          "emptyStateCopy",
        ]);
        if (mode) {
          allowed.add("recruitmentMode");
        }
        const next = {};
        Object.keys(meta || {}).forEach((key) => {
          if (allowed.has(key)) next[key] = meta[key];
        });
        return next;
      };
      const isRecruitmentTemplate = (inferredTemplate || "").toLowerCase() === "recruitment";
      const recruitmentModeValue = isRecruitmentTemplate ? recruitmentModeState : null;
      setBlueprint({
        ...data,
        template: inferredTemplate,
        templateMeta: isRecruitmentTemplate
          ? sanitizeRecruitmentMeta(normalizedMeta, recruitmentModeValue)
          : normalizedMeta,
        bookingSchedules: Array.isArray(data.bookingSchedules) ? data.bookingSchedules : undefined,
      });
      if ((inferredTemplate || "").toLowerCase() === "recruitment") {
        const sections = Array.isArray(data.components)
          ? data.components.filter(
              (component) => (component?.type || "").toLowerCase() === "qualification_section"
            )
          : [];
        setDefaultRecruitmentSections(cloneJson(sections));
      } else {
        setDefaultRecruitmentSections(null);
      }
      if (!availabilityApiUrl && data.availabilityApiUrl) {
        setAvailabilityApiUrl(data.availabilityApiUrl);
      }
      if (data.bookingSettings) {
        setBookingSettingsInput((prev) => ({
          resourceLabel: data.bookingSettings.resourceLabel || prev.resourceLabel,
          slotDurationMinutes:
            data.bookingSettings.slotDurationMinutes || prev.slotDurationMinutes,
          maxSimultaneousBookings:
            data.bookingSettings.maxSimultaneousBookings || prev.maxSimultaneousBookings,
          notes: data.bookingSettings.notes || prev.notes,
        }));
      }
      setPublishResult(null);
      fetchPublishes();
      const nextStatus = (data.aiStatus || "ok").toLowerCase();
      setAiStatus(nextStatus);
      setAiError(data.aiError || "");
    };

    const pollBlueprintJob = async (jobId, bearer) => {
      const maxAttempts = 60;
      const delayMs = 2000;
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const statusUrl = `${API_URL}/${encodeURIComponent(
          business.businessId
        )}?action=blueprint_status&jobId=${encodeURIComponent(jobId)}`;
        const statusRes = await fetch(statusUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${bearer}`,
          },
        });
        if (!statusRes.ok) {
          const message = (await statusRes.text()) || "Unable to fetch job status.";
          throw new Error(message);
        }
        const record = await statusRes.json();
        const status = (record.status || "").toLowerCase();
        if (status === "succeeded") {
          return record;
        }
        if (status === "failed") {
          throw new Error(record.error || "Blueprint generation failed.");
        }
        await new Promise((resolve) => {
          asyncPollRef.current = setTimeout(resolve, delayMs);
        });
      }
      throw new Error("Blueprint generation timed out. Please try again.");
    };

    try {
      const { tokens } = await fetchAuthSession();
      const bearer = resolveAuthToken(tokens);
      if (!bearer) throw new Error("Session expired. Please sign in again.");
      const url = `${API_URL}/${encodeURIComponent(
        business.businessId
      )}?action=generate_blueprint_async`;
      const profile = fallbackProfiles.find((p) => p.key === profileKey) || fallbackProfiles[0];
      let finalPrompt =
        includeSettings && settingsSummary
          ? `${trimmed}\n\nUse the saved settings when relevant:\n${settingsSummary}`
          : `${trimmed}${
              profile && profile.prompt
                ? `\n\nIf details are missing, apply this fallback profile: ${profile.prompt}`
                : ""
            }`;
      if (systemType === "recruitment" && rec2RecMode) {
        finalPrompt = `${finalPrompt}\n\nThis is a confidential recruiter profile, not a job application. Do not contact the current employer, and only share details with approval.`;
      }
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearer}`,
        },
        body: JSON.stringify({
          prompt: finalPrompt,
          includeSettings,
          jobDescription: trimmed,
          template: activeTab === "home" ? undefined : systemType,
          recruitmentMode: systemType === "recruitment" ? recruitmentModeState : undefined,
          flowType:
            systemType === "recruitment" && apprenticeshipEnquiryFlow
              ? "apprenticeship_enquiry"
              : undefined,
        }),
      });
      if (!res.ok) {
        const message = (await res.text()) || "Unable to generate system.";
        throw new Error(message);
      }
      const job = await res.json();
      if (!job.jobId) {
        throw new Error("Missing job id from async generator.");
      }
      const record = await pollBlueprintJob(job.jobId, bearer);
      let resultPayload = record.result;
      if (typeof resultPayload === "string") {
        try {
          resultPayload = JSON.parse(resultPayload);
        } catch (err) {
          resultPayload = null;
        }
      }
      const blueprintData =
        resultPayload && typeof resultPayload === "object" && resultPayload.blueprint
          ? resultPayload.blueprint
          : resultPayload;
      if (!blueprintData || typeof blueprintData !== "object") {
        throw new Error("Blueprint generation completed, but no data was returned.");
      }
      applyGeneratedBlueprint(blueprintData);
    } catch (err) {
      console.error("System generator failed:", err);
      setBlueprint(null);
      setError(err.message || "Unable to generate system.");
      setAiStatus("error");
      setAiError(err.message || "Unable to generate system.");
    } finally {
      setIsBusy(false);
    }
  };

  const splitListInput = (value) =>
    String(value || "")
      .split(/,|\n/)
      .map((item) => item.trim())
      .filter(Boolean);

  const sanitizeComponentsForPublish = (components) => {
    if (!Array.isArray(components)) return components;
    return components.map((component) => {
      if ((component?.type || "").toLowerCase() !== "qualification_section") {
        return component;
      }
      const questions = Array.isArray(component.questions) ? component.questions : [];
      const cleanedQuestions = questions.map((question) => {
        if (!question || typeof question !== "object") return question;
        const options = Array.isArray(question.options) ? question.options : [];
        const blockerAnswers = Array.isArray(question.blockerAnswers)
          ? question.blockerAnswers
          : [];
        const optionsFromText =
          question.optionsText && options.length === 0
            ? splitListInput(question.optionsText)
            : options;
        const blockersFromText =
          question.blockerAnswersText && blockerAnswers.length === 0
            ? splitListInput(question.blockerAnswersText)
            : blockerAnswers;
        const { optionsText, blockerAnswersText, ...rest } = question;
        return {
          ...rest,
          ...(optionsFromText.length ? { options: optionsFromText } : {}),
          ...(blockersFromText.length ? { blockerAnswers: blockersFromText } : {}),
        };
      });
      return { ...component, questions: cleanedQuestions };
    });
  };

  const sanitizeRecruitmentMetaForPublish = (meta, recruitmentModeValue) => {
    const allowed = new Set([
      "label",
      "kind",
      "supportedPayments",
      "supported_payments",
      "heroTitle",
      "heroSubtitle",
      "intro",
      "serviceBadgeLabel",
      "nextStepEyebrow",
      "nextStepTitle",
      "nextStepSubtitle",
      "metaDescription",
      "jobTitle",
      "roleTitle",
      "companyName",
      "candidateName",
      "recruiterName",
      "location",
      "employmentType",
      "salaryRange",
      "validUntil",
      "validUntilLabel",
      "department",
      "roleLabel",
      "salaryLabel",
      "organisationLabel",
      "locationLabel",
      "selectPlaceholder",
      "sectionFallbackTitle",
      "questionFallbackLabel",
      "shortAnswerPlaceholder",
      "validUntilPrefix",
      "timeBadgeTemplate",
      "flowType",
      "contactRequired",
      "nameRequired",
      "contactPrefillName",
      "contactPrefillEmail",
      "contactPrefillPhone",
      "contactPrefillMessage",
      "leadFormTitle",
      "leadFormSubtitle",
      "leadFormSubmitLabel",
      "leadFormNameLabel",
      "leadFormNamePlaceholder",
      "leadFormEmailLabel",
      "leadFormEmailPlaceholder",
      "leadFormPhoneLabel",
      "leadFormPhonePlaceholder",
      "leadFormDisclaimer",
      "leadFormReviewNote",
      "confirmationTitle",
      "confirmationSubtitle",
      "confirmationNextSteps",
      "confirmationSecondary",
      "emptyStateCopy",
    ]);
    if (recruitmentModeValue) {
      allowed.add("recruitmentMode");
    }
    const next = {};
    Object.keys(meta || {}).forEach((key) => {
      if (allowed.has(key)) next[key] = meta[key];
    });
    return next;
  };

  const handleResetRecruitmentQuestions = useCallback(() => {
    if (!defaultRecruitmentSections || !setBlueprint) return;
    setBlueprint((prev) => {
      if (!prev) return prev;
      const components = Array.isArray(prev.components) ? [...prev.components] : [];
      const resetQueue = cloneJson(defaultRecruitmentSections);
      const nextComponents = components.map((component) => {
        if ((component?.type || "").toLowerCase() !== "qualification_section") {
          return component;
        }
        return resetQueue.length ? resetQueue.shift() : component;
      });
      while (resetQueue.length) {
        nextComponents.push(resetQueue.shift());
      }
      return { ...prev, components: nextComponents };
    });
  }, [defaultRecruitmentSections]);

  const handleApplyLocalServiceQuoteTemplate = useCallback(() => {
    const nextBlueprint = buildLocalServiceQuoteBlueprint(business);
    setBlueprint(nextBlueprint);
    const sections = Array.isArray(nextBlueprint.components)
      ? nextBlueprint.components.filter(
          (component) => (component?.type || "").toLowerCase() === "qualification_section"
        )
      : [];
    setDefaultRecruitmentSections(cloneJson(sections));
    setIntakeMode(true);
    setRec2RecMode(false);
    setApprenticeshipEnquiryFlow(false);
    setError("");
    setPublishError("");
    setAiStatus("");
    setAiError("");
    setPublishResult(null);
  }, [business]);

  const handlePublish = async () => {
    if (!blueprint || !business?.businessId) return;
    if (atPublishLimit) {
      setError("You've reached the live system limit for your plan. Upgrade to publish more.");
      return;
    }
    if (intakeMode && (aiStatus || "").toLowerCase() === "error") {
      setError("AI failed to generate intake questions. Please retry before publishing.");
      return;
    }
    const itemCount = Array.isArray(blueprint.items) ? blueprint.items.length : 0;
    const isRecruitment = (blueprint.template || "").toLowerCase() === "recruitment";
    if (!isRecruitment && itemCount === 0) {
      setError("Add at least one item before publishing this system.");
      return;
    }
    const recruitmentComponents = (blueprint.components || []).filter(
      (component) => (component?.type || "").toLowerCase() === "qualification_section"
    );
    if (isRecruitment) {
      const questionCount = recruitmentComponents.reduce((total, section) => {
        const questions = Array.isArray(section?.questions) ? section.questions : [];
        const count = questions.filter((q) => (q?.category || "") !== "contact").length;
        return total + count;
      }, 0);
      if (questionCount < 2) {
        setError("Add at least 2 questions before publishing.");
        return;
      }
      if (questionCount > 10) {
        setError("Please limit qualification questions to 10 or fewer.");
        return;
      }
    }
    const normalizedAvailabilityUrl = (availabilityApiUrl || "").trim();

    const blueprintToPublish = {
      ...blueprint,
      components: sanitizeComponentsForPublish(blueprint.components || []),
    };

    const deriveStoreContext = () => {
      const active = (menuPublishes.active || [])[0];
      return {
        storeId: blueprintToPublish.storeId || active?.storeId || active?.store_id || "",
        storeSlug: blueprintToPublish.storeSlug || active?.storeSlug || active?.store_slug || active?.slug || "",
      };
    };
    const storeCtx = deriveStoreContext();
    const chosenImages =
      imageUploadUrls.length > 0
        ? imageUploadUrls.slice(0, MAX_IMAGE_COUNT)
        : blueprintToPublish.imageUrls || [];
    const isCoaching = (blueprintToPublish.template || "").toLowerCase() === "coaching";
    const isDefaultMenu = ["default", "menu"].includes(
      (blueprintToPublish.template || "").toLowerCase()
    );
    const itemsWithImages =
      isDefaultMenu && chosenImages.length
        ? (blueprintToPublish.items || []).map((item, idx) => {
            if (idx < chosenImages.length) {
              return { ...item, imageUrl: chosenImages[idx], image: chosenImages[idx] };
            }
            return item;
          })
        : blueprintToPublish.items || [];

    const templateMetaBase = blueprintToPublish.templateMeta || {};
    const recruitmentMode =
      (blueprintToPublish.template || "").toLowerCase() === "recruitment"
        ? recruitmentModeState
        : undefined;
    const templateMetaWithCopy =
      recruitmentMode != null
        ? sanitizeRecruitmentMetaForPublish(templateMetaBase, recruitmentMode)
        : {
            ...templateMetaBase,
            heroSubtitle: templateMetaBase.heroSubtitle || "",
            catalogTitle: templateMetaBase.catalogTitle || "",
            catalogSubtitle: templateMetaBase.catalogSubtitle || "",
            catalogSearchPlaceholder: templateMetaBase.catalogSearchPlaceholder || "",
          };

    const galleryMeta =
      chosenImages.length > 0
        ? {
            ...templateMetaWithCopy,
            galleryImages: chosenImages,
            coachingImages: isCoaching
              ? chosenImages
              : templateMetaBase.coachingImages || [],
            ecommerceImages:
              (blueprintToPublish.template || "").toLowerCase() === "ecommerce"
                ? chosenImages
                : templateMetaBase.ecommerceImages || [],
          }
        : templateMetaWithCopy;

    const blueprintWithStore = {
      ...blueprintToPublish,
      ...storeCtx,
      availabilityApiUrl: normalizedAvailabilityUrl,
      items: itemsWithImages,
      imageUrls: chosenImages,
      logoUrl: logoUploadUrl || blueprintToPublish.logoUrl || business?.logoUrl || "",
      digitalProductUrl:
        pdfUploadUrl ||
        blueprintToPublish.digitalProductUrl ||
        blueprintToPublish.pdfUrl ||
        business?.digitalProductUrl ||
        business?.pdfUrl ||
        "",
      pdfUrl:
        pdfUploadUrl ||
        blueprintToPublish.pdfUrl ||
        blueprintToPublish.digitalProductUrl ||
        business?.pdfUrl ||
        business?.digitalProductUrl ||
        "",
      templateMeta: galleryMeta,
      bookingSettings: {
        ...(blueprintToPublish.bookingSettings || {}),
        ...bookingSettingsInput,
      },
      ...(blueprintToPublish.bookingSchedules
        ? { bookingSchedules: blueprintToPublish.bookingSchedules }
        : {}),
    };

    setIsPublishing(true);
    setError("");
    setPublishError("");
    try {
      const { tokens } = await fetchAuthSession();
      const bearer = resolveAuthToken(tokens);
      if (!bearer) throw new Error("Session expired. Please sign in again.");
      const url = `${API_URL}/${encodeURIComponent(
        business.businessId
      )}?action=publish_blueprint`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearer}`,
        },
        body: JSON.stringify({ prompt, blueprint: blueprintWithStore }),
      });
      const text = await res.text();
      let data = {};
      if (text) {
        try {
          data = JSON.parse(text);
        } catch (parseErr) {
          console.warn("Unable to parse publish response JSON:", parseErr);
          data = { message: text };
        }
      }
      if (!res.ok) {
        throw new Error(data.error || data.message || "Unable to publish system.");
      }
      setPublishResult(data);
      // Pull latest publishes from source of truth
      fetchPublishes();
    } catch (err) {
      console.error("System publish failed:", err);
      setPublishResult(null);
      const message = err.message || "Unable to publish system.";
      setPublishError(message);
      setError(message);
    } finally {
      setIsPublishing(false);
    }
  };

  useEffect(() => {
    fetchPublishes();
  }, [fetchPublishes]);

  useEffect(() => {
    if (publishResult?.publicUrl) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 1800);
      return () => clearTimeout(timer);
    }
  }, [publishResult?.publicUrl]);

  useEffect(() => {
    if (!publishResult?.publicUrl || systemType !== "recruitment") return;
    handleCopyLink(publishResult.publicUrl);
  }, [handleCopyLink, publishResult?.publicUrl, systemType]);

  return (
    <div className={`system-gen ${isRecruitmentPath ? "system-gen--recruitment" : ""}`}>
      {showConfetti && (
        <div className="system-gen__confetti">
          {Array.from({ length: 50 }).map((_, idx) => (
            <span
              key={idx}
              style={{
                "--left": `${Math.random() * 100}%`,
                "--delay": `${Math.random() * 0.6}s`,
                "--duration": `${1.5 + Math.random() * 0.9}s`,
              }}
            />
          ))}
        </div>
      )}
      <div className="system-gen__shell">
        <header className="system-gen__header">
          <button
            type="button"
            className="system-gen__back"
            onClick={() => navigate(-1)}
          >
            ← Back
          </button>
          <div className="system-gen__mode-tabs" role="tablist" aria-label="System type">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "home"}
              aria-label="All systems"
              className={`system-gen__mode-tab ${
                activeTab === "home" ? "system-gen__mode-tab--active" : ""
              }`}
              onClick={() => navigate("/systems")}
            >
              🌌
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "rec2rec"}
              aria-label="Rec2Rec pre-screen systems"
              className={`system-gen__mode-tab ${
                activeTab === "rec2rec" ? "system-gen__mode-tab--active" : ""
              }`}
              onClick={() => navigate("/systems/rec2rec")}
            >
              🤝
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "recruitment"}
              aria-label="Recruitment systems"
              className={`system-gen__mode-tab ${
                activeTab === "recruitment" ? "system-gen__mode-tab--active" : ""
              }`}
              onClick={() => navigate("/systems/recruitment")}
            >
              🧑‍💼
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "restaurant"}
              aria-label="Restaurant ordering systems"
              className={`system-gen__mode-tab ${
                activeTab === "restaurant" ? "system-gen__mode-tab--active" : ""
              }`}
              onClick={() => navigate("/systems/restaurant")}
            >
              🍽️
            </button>
          </div>
          <div className="system-gen__brand-row">
            <div className="system-gen__brand">
              <img src={Logo} alt="Layerbound" className="system-gen__logo" />
              <div>
                <h1>{copy.title}</h1>
              </div>
            </div>
            {business?.planType && (
              <span className="system-gen__pill system-gen__pill--plan">
                {business.planType.toLowerCase() === "lifetime" ? "Lifetime plan" : `${business.planType} plan`}
              </span>
            )}
          </div>
          <p className="system-gen__subtitle">
            {copy.subtitle}
          </p>
          {isBusy && (
            <div className="system-gen__status-pill" role="status" aria-live="polite">
              Generating draft…
            </div>
          )}
        </header>

        {publishResult?.publicUrl && systemType === "recruitment" && (
          <div className="system-gen__publish-banner" role="status">
            <div className="system-gen__publish-main">
              <span className="system-gen__publish-label">
                {isLocalServiceQuote ? "Live quote link ready" : "Live link ready"}
              </span>
              <a
                href={publishResult.publicUrl}
                target="_blank"
                rel="noreferrer"
                className="system-gen__publish-url"
              >
                {publishResult.publicUrl}
              </a>
            </div>
            <div className="system-gen__publish-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => handleCopyLink(publishResult.publicUrl)}
              >
                {copyStatus || "Copy link"}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-secondary--ghost"
                onClick={() => setIsPreviewOpen(true)}
              >
                {isLocalServiceQuote
                  ? "Preview quote page"
                  : intakeMode
                    ? "Preview intake view"
                    : "Preview candidate view"}
              </button>
            </div>
          </div>
        )}

        {aiStatus && (
          <div
            className={`system-gen__ai-banner system-gen__ai-banner--${aiStatus}`}
            role="status"
          >
            {aiStatus === "ok" && "AI generated the draft successfully."}
            {aiStatus === "fallback" && "AI not used. Showing fallback copy."}
            {aiStatus === "disabled" && "AI disabled. Showing fallback copy."}
            {aiStatus === "error" && "AI failed to generate a valid draft."}
            {aiError && aiStatus === "error" && (
              <span className="system-gen__ai-banner-detail">{aiError}</span>
            )}
          </div>
        )}

        {(blueprint?.template || "").toLowerCase() === "recruitment" && (
          <div className="system-gen__step">
            <span className="system-gen__step-label">01</span>
            <span className="system-gen__step-title">
              {isLocalServiceQuote ? "Quote brief" : intakeMode ? "Intake brief" : "Role brief"}
            </span>
          </div>
        )}
        <div className="system-gen__chat">
          <div className="chat-bubble chat-bubble--user">
            <div className="system-gen__composer">
              <div className="system-gen__composer-row">
                {!isRecruitmentPath && (
                  <button
                    type="button"
                    className="system-gen__attach-btn"
                    onClick={() => setShowAttachMenu((prev) => !prev)}
                    disabled={isBusy || assetBusy}
                    aria-label="Upload logo or PDF"
                  >
                    <span className="system-gen__attach-icon">+</span>
                  </button>
                )}
                <textarea
                  className="system-gen__input"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={copy.placeholder}
                  rows={isRecruitmentPath ? 6 : 8}
                  disabled={isBusy}
                  maxLength={2000}
                />
              </div>
              {!isRecruitmentPath && showAttachMenu && (
                <div className="system-gen__attach-menu">
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={assetBusy || isBusy}
                  >
                    Upload logo
                  </button>
                  <button
                    type="button"
                    onClick={() => pdfInputRef.current?.click()}
                    disabled={assetBusy || isBusy}
                  >
                    Upload PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => imageInputRef.current?.click()}
                    disabled={assetBusy || isBusy || imageUploadUrls.length >= MAX_IMAGE_COUNT}
                  >
                    Upload image ({MAX_IMAGE_COUNT - imageUploadUrls.length} left)
                  </button>
                </div>
              )}
              {!isRecruitmentPath && (logoUploadUrl || pdfUploadUrl || imageUploadUrls.length > 0) && (
                <div className="system-gen__attachment-previews">
                  {logoUploadUrl && (
                    <div className="system-gen__attachment-chip">
                      <div className="system-gen__attachment-thumb">
                        <img src={logoUploadUrl} alt="Logo preview" />
                      </div>
                      <span>Logo ready</span>
                    </div>
                  )}
                  {pdfUploadUrl && (
                    <div className="system-gen__attachment-chip">
                      <div className="system-gen__attachment-thumb system-gen__attachment-thumb--pdf">PDF</div>
                      <a href={pdfViewUrl || pdfUploadUrl} target="_blank" rel="noreferrer">
                        View PDF
                      </a>
                    </div>
                  )}
                  {imageUploadUrls.map((url, idx) => (
                    <div className="system-gen__attachment-chip" key={url}>
                      <div className="system-gen__attachment-thumb">
                        <img src={url} alt={`Upload ${idx + 1}`} />
                      </div>
                      <span>Image {idx + 1}</span>
                    </div>
                  ))}
                </div>
              )}
              {assetBusy && <p className="system-gen__subtitle">Uploading…</p>}
              {!isRecruitmentPath && (
                <>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoSelect}
                    className="system-gen__file-input"
                  />
                  <input
                    ref={pdfInputRef}
                    type="file"
                    accept="application/pdf"
                    onChange={handlePdfSelect}
                    className="system-gen__file-input"
                  />
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageSelect}
                    className="system-gen__file-input"
                  />
                </>
              )}
            </div>
            {error && <p className="system-gen__error">{error}</p>}
            <p className="system-gen__subtitle">
              {prompt.length}/2000 characters. {copy.promptHelp}
            </p>
            {systemType === "recruitment" && !isSystemsHome && (
              <div className="system-gen__template-actions">
                <button
                  type="button"
                  className="btn btn-secondary btn-secondary--ghost"
                  onClick={handleApplyLocalServiceQuoteTemplate}
                  disabled={isBusy}
                >
                  Use carpet-cleaning quote template
                </button>
              </div>
            )}
            {!hideAdvancedOptions && (
              <div className="system-gen__accordion">
                <button
                  type="button"
                  className="system-gen__accordion-toggle"
                  onClick={() => setShowSettings((prev) => !prev)}
                >
                  <span>Advanced options</span>
                  <span className={`chevron ${showSettings ? "chevron--open" : ""}`} />
                </button>
                {showSettings && (
                  <div className="system-gen__toggle">
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={includeSettings}
                        onChange={(e) => setIncludeSettings(e.target.checked)}
                      />
                      <span className="toggle__label">Use my saved settings (hours, timezone, schedules)</span>
                    </label>
                    {systemType === "recruitment" && !isSystemsHome && !isLocalServiceQuote && (
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={rec2RecMode}
                          onChange={(e) => {
                            const next = e.target.checked;
                            setRec2RecMode(next);
                            if (next) setIntakeMode(false);
                          }}
                          disabled={intakeMode}
                        />
                        <span className="toggle__label">Use confidential Rec2Rec pre-screen wording</span>
                      </label>
                    )}
                    {systemType === "recruitment" && !isSystemsHome && !isLocalServiceQuote && (
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={apprenticeshipEnquiryFlow}
                          onChange={(e) => {
                            const next = e.target.checked;
                            setApprenticeshipEnquiryFlow(next);
                            if (next) {
                              setIntakeMode(true);
                              setRec2RecMode(false);
                            }
                          }}
                          disabled={rec2RecMode}
                        />
                        <span className="toggle__label">Use apprenticeship enquiry flow</span>
                      </label>
                    )}
                    {systemType === "recruitment" && !isSystemsHome && !isLocalServiceQuote && (
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={intakeMode}
                          onChange={(e) => {
                            const next = e.target.checked;
                            setIntakeMode(next);
                            if (next) setRec2RecMode(false);
                            if (!next) setApprenticeshipEnquiryFlow(false);
                          }}
                          disabled={rec2RecMode}
                        />
                        <span className="toggle__label">Use intake / lead‑gen wording</span>
                      </label>
                    )}
                    {includeSettings && settingsSummary && (
                      <div className="system-gen__settings-chip">
                        {settingsSummary.slice(0, 180)}{settingsSummary.length > 180 ? "…" : ""}
                      </div>
                    )}
                    {!includeSettings && (
                      <div className="system-gen__settings-chip system-gen__settings-chip--muted">
                        Using prompt only; saved settings will be ignored for this generation.
                      </div>
                    )}
                    {!includeSettings && (
                      <div className="system-gen__profile">
                        <label>
                          Fallback booking profile
                          <select value={profileKey} onChange={(e) => setProfileKey(e.target.value)}>
                            {fallbackProfiles.map((p) => (
                              <option key={p.key} value={p.key}>
                                {p.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <p className="system-gen__subtitle">
                          {fallbackProfiles.find((p) => p.key === profileKey)?.hint ||
                            "We’ll auto-detect a sensible schedule if the prompt is vague."}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <button
              type="button"
              className={`btn btn-primary system-gen__button ${
                isRecruitmentPath ? "system-gen__button--wide" : ""
              }`}
              disabled={!prompt.trim() || isBusy || atPublishLimit || atDailyPublishLimit}
              onClick={handleSubmit}
            >
              {isBusy ? "Generating…" : copy.submitLabel}
            </button>
            {atPublishLimit && (
              <p className="system-gen__error">
                Free plan allows one live link. Archive/delete it to generate a new one.
              </p>
            )}
            {atDailyPublishLimit && (
              <p className="system-gen__error">
                Daily publish limit reached ({dailyPublishLimit} per day). Try again tomorrow.
              </p>
            )}
            {!isRecruitmentPath && (
              <div className="system-gen__quick-prompts">
                {copy.quickPrompts.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => setPrompt(suggestion)}
                    className="pill"
                    disabled={isBusy}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {systemType === "recruitment" ? (
          <RecruitmentMetaEditor
            blueprint={blueprint}
            setBlueprint={setBlueprint}
            business={business}
            rec2RecMode={rec2RecMode}
          />
        ) : (
          <>
            <TemplateMetaEditor
              blueprint={blueprint}
              setBlueprint={setBlueprint}
              business={business}
              rec2RecMode={rec2RecMode}
              intakeMode={intakeMode}
            />
            <ContextSuggestionsEditor blueprint={blueprint} setBlueprint={setBlueprint} />
          </>
        )}

        <BlueprintPreview
          blueprint={blueprint}
          setBlueprint={setBlueprint}
          onResetRecruitmentQuestions={handleResetRecruitmentQuestions}
          canResetRecruitmentQuestions={Boolean(defaultRecruitmentSections?.length)}
          minimalRecruitment={systemType === "recruitment"}
          intakeMode={intakeMode}
        />
        {!isRecruitmentPath && <UsagePanel usage={publishUsage} />}
        {blueprint && systemType === "recruitment" && (
          <div className="system-gen__step">
            <span className="system-gen__step-label">03</span>
            <span className="system-gen__step-title">
              {isLocalServiceQuote ? "Publish quote link" : "Publish link"}
            </span>
          </div>
        )}
        {blueprint && (
          <div className="system-gen__actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handlePublish}
              disabled={isPublishing || atPublishLimit}
            >
              {atPublishLimit
                ? "Publish limit reached"
                : isPublishing
                  ? "Publishing…"
                  : isLocalServiceQuote
                    ? "Publish quote link"
                    : "Publish system"}
            </button>
            {atPublishLimit && (
              <p className="system-gen__subtitle system-gen__error">
                You’ve hit your live system cap for this plan. Archive a system or upgrade to publish again.
              </p>
            )}
            {publishError && !atPublishLimit && (
              <p className="system-gen__subtitle system-gen__error">{publishError}</p>
            )}
          </div>
        )}
        {publishResult && (
          <div className="system-gen__panel system-gen__publish-result">
            <h3>{isLocalServiceQuote ? "Live quote link" : "Live link"}</h3>
            {publishResult.publicUrl ? (
              <a
                href={publishResult.publicUrl}
                target="_blank"
                rel="noreferrer"
                className="system-gen__link"
              >
                {publishResult.publicUrl}
              </a>
            ) : (
              <p>Your storefront has been published.</p>
            )}
            {publishResult.publicUrl && systemType === "recruitment" && (
              <>
                <div className="system-gen__publish-inline-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleCopyLink(publishResult.publicUrl)}
                  >
                    {copyStatus || "Copy link"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-secondary--ghost"
                    onClick={() => setIsPreviewOpen(true)}
                  >
                    {isLocalServiceQuote
                      ? "Preview quote page"
                      : intakeMode
                        ? "Preview intake view"
                        : "Preview candidate view"}
                  </button>
                </div>
                {trackedQuoteLinks.length > 0 && (
                  <div className="system-gen__tracked-links">
                    {trackedQuoteLinks.map((link) => (
                      <div key={link.key} className="system-gen__tracked-link">
                        <span className="system-gen__tracked-link-label">{link.label}</span>
                        <a href={link.url} target="_blank" rel="noreferrer" className="system-gen__link">
                          {link.url}
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            {publishResult.aiUsage && (
              <p className="system-gen__usage">
                AI credits remaining: {publishResult.aiUsage.monthlyRemaining} /
                {publishResult.aiUsage.monthlyLimit}
                {publishResult.aiUsage.resetsAt && (
                  <>
                    {" "}
                    (resets{" "}
                    {new Date(publishResult.aiUsage.resetsAt).toLocaleDateString()}
                    )
                  </>
                )}
              </p>
            )}
          </div>
        )}
        {isPreviewOpen && publishResult?.publicUrl && (
          <div
            className="system-gen__modal-backdrop"
            role="dialog"
            aria-modal="true"
            onClick={() => setIsPreviewOpen(false)}
          >
            <div
              className="system-gen__modal"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="system-gen__modal-header">
                <div>
                  <p className="system-gen__eyebrow">Preview</p>
                  <h3>{isLocalServiceQuote ? "Quote page" : intakeMode ? "Intake view" : "Candidate view"}</h3>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary btn-secondary--ghost"
                  onClick={() => setIsPreviewOpen(false)}
                >
                  Close
                </button>
              </div>
              <iframe
                title={isLocalServiceQuote ? "Quote page preview" : intakeMode ? "Intake preview" : "Candidate preview"}
                src={publishResult.publicUrl}
                className="system-gen__modal-frame"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SystemGeneratorPage;

function buildSettingsSummary(business) {
  if (!business) return "";
  const parts = [];
  if (business.timeZone) parts.push(`Timezone ${business.timeZone}`);
  const oh = business.openingHours || {};
  const openDays = Object.entries(oh)
    .filter(([, v]) => v && v !== "closed")
    .map(([d]) => d.slice(0, 3))
    .join(", ");
  if (openDays) parts.push(`Opening days: ${openDays}`);
  if (Array.isArray(business.bookingSchedules) && business.bookingSchedules.length) {
    const first = business.bookingSchedules[0];
    const schedLabel = `${business.bookingSchedules.length} schedule${business.bookingSchedules.length > 1 ? "s" : ""}`;
    const timeLabel = first.dailyStartTime && first.dailyEndTime ? `${first.dailyStartTime}-${first.dailyEndTime}` : "";
    parts.push(`${schedLabel} (e.g. ${first.name || first.resourceLabel || "schedule"} ${timeLabel})`);
  }
  return parts.join(" · ");
}

function UsagePanel({ usage }) {
  if (!usage) return null;
  const remaining = usage.monthlyRemaining ?? usage.remaining ?? null;
  const limit = usage.monthlyLimit ?? usage.limit ?? null;
  return (
    <div className="system-gen__usage-card system-gen__usage-card--accent">
      <div>
        <p className="system-gen__eyebrow">Live links</p>
        <p className="system-gen__subtitle">
          {usage.subtitle
            ? usage.subtitle
            : remaining !== null && limit !== null
              ? `${remaining} remaining of ${limit} this month`
              : "Usage refreshed monthly."}
        </p>
      </div>
      {usage.resetsAt && (
        <div className="system-gen__pill">
          Resets {new Date(usage.resetsAt).toLocaleDateString()}
        </div>
      )}
    </div>
  );
}
